(ns ductile.ui.truck-booking
  (:require-macros [ductile.ui.macros :as macros])
  (:require ["luxon" :as luxon :refer [DateTime]]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.compound-config :as cc]
            [ductile.time.format :as time-format]
            [ductile.ui.auth :as auth]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.forms :as forms.ui]
            [ductile.ui.http :as http]
            [ductile.ui.locale :refer [tr trn get-current-iso-locale]]
            [ductile.ui.router :as router]
            [ductile.ui.scene :as scene]
            [ductile.ui.util :as util]
            [inside-out.forms :as forms]
            [inside-out.reagent :refer [with-form]]
            [kitchen-async.promise :as p]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.log :as log]
            [reagent.core :as r])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]))

(def international-phone-prefixes
  (macros/clojure-var 'ductile.truck-booking.common/international-phone-prefixes))

(defn random-driver-name []
  (macros/clojure-var 'ductile.truck-booking.common/random-driver-name))


(defn random-unloading
  ([] (random-unloading {}))
  ([opts] (macros/clojure-var 'ductile.truck-booking.common/random-unloading opts)))

(defn pad [p s]
  (.slice (str p s) -2))

(defn at
  ([datetime h] (at datetime h 0 0))
  ([datetime h m] (at datetime h m 0))
  ([datetime h m s] (at datetime h m s 0))
  ([datetime h m s nano] (doto (js/Date. datetime)
                           (.setHours h m s nano))))

(defn hour->datetime [h]
  (at (js/Date.) h))

(defn after? [date arg]
  (> (.getTime date) (.getTime arg)))

(defn before? [date arg]
  (after? arg date))

(defn between? [start t end]
  (and (after? t start)
       (before? t end)))

(defn plus [datetime [amount unit]]
  (doto (js/Date.)
    (.setTime (+ (.getTime datetime)
                 (* amount (case unit
                             :days (* 60 60 24 1000)
                             :hours (* 60 60 1000)
                             :minutes (* 60 1000)
                             :seconds (* 1000)
                             :milliseconds 1))))))

(defn minus [datetime [amount unit]]
  (plus datetime [(* -1 amount) unit]))


(defn minutes+seconds-between
  "Minutes and seconds between two given instants, as 'mm:ss' String"
  [instant1 instant2]
  (let [difference (int (/ (- (.getTime instant1)
                              (.getTime instant2))
                           1000))
        secs (js/Math.abs difference)]
    (str (when (neg? difference) "-")
         (pad "0" (quot secs 60)) ":"
         (pad "0" (rem secs 60)))))

(defn ^:private format-slot-time [{:as opts :keys [break? date?]} scheduled-at duration-or-scheduled-to]
  [:<>
   (when date?
     [:<> (time-format/->str (merge opts {:format :date :style :full}) scheduled-at) ", "])
   (time-format/->str (merge opts {:format :time})
                      scheduled-at)
   " - "
   (when break? [:br.md:hidden])
   (time-format/->str (merge opts {:format :time})
                      (if (vector? duration-or-scheduled-to)
                        (plus scheduled-at duration-or-scheduled-to)
                        duration-or-scheduled-to))])

(defn new-unloading [scheduled-at duration]
  (when-not scheduled-at
    (throw (ex-info "Can't create Unloading without scheduled-at"
                    {:scheduled-at scheduled-at :duration duration})))
  {:unloading/scheduled-duration duration
   :unloading/scheduled-datetime scheduled-at})

(defn select-slot [compound-name previous done]
  [:div.relative.bg-white.md:rounded-lg.max-w-3xl.mx-auto.mt-4.md:my-6.shadow-lg
   [:div.flex-auto.overflow-y-auto
    [:div
     [:div.flex.justify-between.items-center.py-4.px-6.border-b
      [:h4.mt-0.text-base.font-bold (tr "Select a slot")]
      [:div.flex.items-center.leading-none
       [:div.rounded-full.w-2.h-2.bg-green-500.mr-1]
       [:div.font-medium.text-xs.text-green-500.mr-3 (tr "Free")]
       [:div.rounded-full.w-2.h-2.bg-yellow-500.mr-1]
       [:div.font-medium.text-xs.text-yellow-500.mr-3 (tr "Few free")]
       [:div.rounded-full.w-2.h-2.bg-red-500.mr-1]
       [:div.font-medium.text-xs.text-red-500 (tr "Full")]]]
     (for [[date-str s] @(deframe/!route-get :slots)]
       (let [date (js/Date. date-str)]
         [:div.p-6.border-b.even:bg-slate-50
          {:key date-str}
          [:div.flex.pb-2.mb-3.md:flex.justify-between.items-center
           [:h2.text-base.font-bold.text-slate-500.leading-none (time-format/->str {:format :date :style :full} date)]]
          (into [:div.grid.grid-cols-3.md:grid-cols-9.gap-4]
                (for [{:keys [datetime duration status]} (sort-by :datetime s)]
                  (let [color (case status
                                :full "red-500"
                                :almost-full "yellow-500"
                                "green-500")]
                    [:div.rounded.border-2.shadow.text-center.py-3.font-bold.cursor-pointer
                     {:key (str datetime)
                      :on-click #(when (or (= :free status)
                                           ;; we allow the internal companies to book even if the slot is full
                                           (contains? (cc/extra-hour-companies compound-name)
                                                      (-> @deframe/!current-user :user/company :company/name)))
                                   (done (new-unloading datetime duration)))
                      :style {:font-size 12}
                      :class [(str "border-" color)
                              (str "text-" color)
                              (if (= :full status)
                                "bg-transparent"
                                "bg-white")
                              (if (= :full status)
                                "opacity-30"
                                "hover:opacity-80")]}
                     (format-slot-time {:break? true} datetime [duration :seconds])])))]))
     [:div.rounded-b-lg.bg-white.py-3.px-6
      [ui/button {:variant :white
                  :size 3
                  :on-click previous} "← " (tr "Back to transport details")]]]]])

(defn steps [items]
  [:nav.mt-4.md:mt-0
   (into
    [:ol.md:flex.md:space-x-8]
    (map-indexed
     (fn [i {:keys [active? done? label route]}]
       [:li.md:flex-1
        [(if route :a :div)
         (cond->
           {:class (concat
                    ["group" "pl-4" "py-2" "flex" "md:flex-col" "border-l-4" "items-center" "md:items-start"
                     "md:pl-0" "md:pt-4" "md:pb-0" "md:border-l-0" "md:border-t-4"]
                    (if (or active? done?)
                      ["border-indigo-600" "hover:border-indigo-800"]
                      ["border-gray-200" "hover:border-gray-300"]))}
           route (assoc :href route))
         (when route
           {:href route})
         [:span.text-xs.font-semibold.tracking-wide.uppercase.mr-4.md:mr-0
          {:class (if (or active? done?)
                    ["text-indigo-600" "group-hover:text-indigo-800"]
                    ["text-gray-500" "group-hover:text-gray-700"])}
          (str (tr "Step") " " (inc i))]
         [:span.text-sm.font-medium label]]])
     items))])

(defn guard [x f] (when (f x) x))

(def phone-number-regex #"\+[0-9]+")

(defn select-vehicle-customer [compound-config {:keys [show-none? value on-value]}]
  (let [choices (cond-> []
                  show-none? (conj {:ui/label (tr "None")
                                    :ui/value "none"})
                  :always (into (map (fn [customer-name]
                                       {:ui/label customer-name
                                        :ui/value customer-name})
                                     (sort (:booking/vehicle-customer-list compound-config)))))
        selected (some #(when (= value (:ui/value %)) %) choices)]
    [combobox/view
     {:value selected
      :placeholder (tr "Select customer")
      :size 3
      :choices choices
      :on-change #(on-value (:ui/value %))}]))

(defn used-vehicle-form-with-customer [compound-config {:as ?vehicle :syms [?customer-name ?vehicle-number]}]
  [:div.md:grid.md:grid-cols-2.gap-3.mb-3
   [:div
    [ui/with-label
     [:span.text-xs (tr "Customer")]
     [:div.w-full.mb-3.text-sm
      [select-vehicle-customer compound-config
       {:value @?customer-name
        :show-none? true
        :on-value #(if-let [name (guard % (partial not= "none"))]
                     (reset! ?customer-name name)
                     (forms/remove-many! ?vehicle))}]]]]
   [ui/with-label
    [:span.text-xs (tr "Number of used vehicles")]
    [forms.ui/input-text ?vehicle-number
     (let [disabled (not @?customer-name)]
       {:type "number" :max 12 :class (str " used-vehicle-number" (when disabled " opacity-50"))
        :disabled disabled
        :on-input #(reset! ?vehicle-number (or (-> (j/get-in % [:target :value])
                                                   js/parseInt
                                                   (guard pos-int?))
                                               0))})]]])

(defn new-vehicle-form-with-customer [compound-config {:as ?vehicle :syms [?customer-name ?vehicle-number]}]
  [:div.md:grid.md:grid-cols-2.gap-3.mb-3
   [:div
    [ui/with-label
     [:span.text-xs (tr "Customer")]
     [:div.w-full.mb-3.text-sm
      [select-vehicle-customer compound-config
       {:value @?customer-name
        :show-none? true
        :on-value #(if-let [name (guard % (partial not= "none"))]
                     (reset! ?customer-name name)
                     (forms/remove-many! ?vehicle))}]]]]
   [ui/with-label
    [:span.text-xs (tr "Number of new vehicles")]
    [forms.ui/input-text ?vehicle-number
     (let [disabled (not @?customer-name)]
       {:type "number" :max 12 :class (str " new-vehicle-number" (when disabled " opacity-50"))
        :disabled disabled
        :on-input #(reset! ?vehicle-number (or (-> (j/get-in % [:target :value])
                                                   js/parseInt
                                                   (guard pos-int?))
                                               0))})]]])

(defn select-vehicle-make [{:keys [show-none? value on-value]}]
  (let [choices (cond-> []
                  show-none? (conj {:ui/label (tr "None")
                                    :ui/value "none"})
                  :always (into (map (fn [make-name]
                                       {:ui/label make-name
                                        :ui/value make-name})
                                     @deframe/!make-names)))
        selected (some #(when (= value (:ui/value %)) %) choices)]
    [combobox/view
     {:value selected
      :placeholder (tr "Select make")
      :size 3
      :choices choices
      :on-change #(on-value (:ui/value %))}]))

(defn new-vehicle-form-with-make [{:as ?vehicle :syms [?make-name ?vehicle-number]}]
  [:div.md:grid.md:grid-cols-2.gap-3.mb-3
   [:div
    [ui/with-label
     [:span.text-xs (tr "Make")]
     [:div.w-full.mb-3.text-sm
      [select-vehicle-make
       {:value @?make-name
        :show-none? true
        :on-value #(if-let [name (guard % (partial not= "none"))]
                     (reset! ?make-name name)
                     (forms/remove-many! ?vehicle))}]]]]
   [ui/with-label
    [:span.text-xs (tr "Number of new vehicles")]
    [forms.ui/input-text ?vehicle-number
     (let [disabled (not @?make-name)]
       {:type "number" :max 12 :class (str " new-vehicle-number" (when disabled " opacity-50"))
        :disabled disabled
        :on-input #(reset! ?vehicle-number (or (-> (j/get-in % [:target :value])
                                                   js/parseInt
                                                   (guard pos-int?))
                                               0))})]]])

(def unloading-validators
  [(fn [{:as _result :keys [transport/vehicles]} _]
     (cond
       (> (count vehicles) 12)
       {:type :invalid
        :content (tr "Too many vehicles on the truck. Max is 12")}))])


(defn transport-details [compound-config previous done]
  (let [unloading @(deframe/!route-get :unloading)]
    (with-form [!form {:transport/driver-name ?driver-name
                       :transport/driver-phone ?driver-phone-number
                       :transport/license-plate ?transport-plate
                       :transport/number ?transport-number
                       :transport/vehicles (vec (concat
                                                 (if (:booking/used-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?used-vehicles-with-customers :many {:vehicle/new? false
                                                                                          :vehicle/number (?vehicle-number :init 1)
                                                                                          :fahrzeug/customer {:customer/name ?customer-name}}))
                                                   (when ?used-vehicles
                                                     (take (js/parseInt ?used-vehicles)
                                                           (repeat {:vehicle/new? false}))))

                                                 (cond
                                                   (:booking/new-vehicle-make-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-make :many {:vehicle/new? true
                                                                                    :vehicle/number (?vehicle-number :init 1)
                                                                                    :vehicle/make {:make/name ?make-name}}))

                                                   (:booking/new-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-customer :many {:vehicle/new? true
                                                                                        :vehicle/number (?vehicle-number :init 1)
                                                                                        :fahrzeug/customer {:customer/name ?customer-name}}))

                                                   :else
                                                   (when ?new-vehicles
                                                     (take (js/parseInt ?new-vehicles)
                                                           (repeat {:vehicle/new? true}))))))}
                :required [?driver-name ?driver-phone-number ?transport-plate]
                :meta {'?driver-name {:init (:transport/driver-name unloading)}
                       '?driver-phone-number {:init (:transport/driver-phone unloading)
                                              :validators [(fn [phone _]
                                                             (when (and phone
                                                                        (not (str/blank? phone))
                                                                        (or (not (re-find phone-number-regex phone))
                                                                            (not (some (fn [p] (str/starts-with? phone p)) international-phone-prefixes))))
                                                               {:type :invalid
                                                                :content (str (tr "The phone number must be in international format: +XXXXX") "…")}))]}
                       '?transport-plate {:init (:transport/license-plate unloading)}
                       '?transport-number {:init (:transport/number unloading)}
                       '?used-vehicles {:init (count (remove :vehicle/new? (:transport/vehicles unloading)))}
                       '?used-vehicles-with-customers {:init (->> (:transport/vehicles unloading)
                                                                  (remove :vehicle/new?)
                                                                  (group-by (comp :customer/name :fahrzeug/customer))
                                                                  (map (fn [[customer-name vehicles]]
                                                                         {'?vehicle-number (count vehicles)
                                                                          '?customer-name customer-name})))}
                       '?new-vehicles {:init (count (filter :vehicle/new? (:transport/vehicles unloading)))}
                       '?new-vehicles-with-customers {:init (->> (:transport/vehicles unloading)
                                                                 (filter :vehicle/new?)
                                                                 (group-by (comp :customer/name :fahrzeug/customer))
                                                                 (map (fn [[customer-name vehicles]]
                                                                        {'?vehicle-number (count vehicles)
                                                                         '?customer-name customer-name})))}
                       '?new-vehicles-with-make {:init (->> (:transport/vehicles unloading)
                                                            (filter :vehicle/new?)
                                                            (group-by (comp :make/name :vehicle/make))
                                                            (map (fn [[make-name vehicles]]
                                                                   {'?vehicle-number (count vehicles)
                                                                    '?make-name make-name})))}}
                :form/validators unloading-validators]
      [:form.flex.flex-col.relative.bg-white.md:rounded-lg.max-w-3xl.mx-auto.mt-4.md:my-6.shadow-lg
       {:on-submit #(do (.preventDefault %)
                        (forms/watch-promise
                         !form
                         (p/let [{:keys [phone-number-valid?]} (http/$lambda 'ductile.sms/$validate {:number @?driver-phone-number})]
                           (do (if (forms/valid? !form)
                                 (done @!form)
                                 (forms/touch! !form))
                               nil)
                           (forms/message :error (tr "Phone number provided is not valid")))))}
       [:div.flex.justify-between.items-center.py-4.px-6.border-b
        [:h4.mt-0.text-base.font-bold (tr "Provide Transport Details")]]
       [:div.bg-slate-50
        [:div.flex-auto.px-6.pt-6
         [:div.flex.items-center.mb-3
          [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
           (tr "Driver")]
          [:div.border-b.flex-auto {:style {:height 1}}]]
         [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
          [:div
           [ui/with-label
            [:span.text-xs (tr "Name")]
            [forms.ui/input-text ?driver-name
             {:class "w-full"}]]]
          [:div
           [ui/with-label
            [:span.text-xs (tr "Mobile phone number")]
            [:div
             [forms.ui/input-text ?driver-phone-number
              {:class "w-full"
               :placeholder (tr "+XXXXX")}]]]]]
         [:div.flex.items-center.mb-3
          [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
           (tr "Truck")]
          [:div.border-b.flex-auto {:style {:height 1}}]]
         [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
          [:div
           [ui/with-label
            [:span.text-xs (tr "License plate")]
            [forms.ui/input-text ?transport-plate
             {:class "w-full"
              :data-testid "license-plate-input"}]]]
          [:div
           [ui/with-label
            [:span.text-xs (tr "Truck number") " (" (tr "optional") ")"]
            [forms.ui/input-text ?transport-number
             {:class "w-full"}]]]]

         (when-not (:booking/skip-used-vehicles? compound-config)
           [:<>
            [:div.flex.items-center.mb-3
             [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
              (tr "Used vehicles")]
             [:div.border-b.flex-auto {:style {:height 1}}]]
            (if (:booking/used-vehicle-customer-required? compound-config)
              [:<>

             ;; show existing new vehicle entries
               (into [:<>]
                     (for [?vehicle ?used-vehicles-with-customers]
                       [used-vehicle-form-with-customer compound-config ?vehicle]))

             ;; add used vehicle by customer
               [:div.md:grid.md:grid-cols-2.gap-3.mb-3
                [ui/with-label
                 [:span.text-xs (tr "Customer")]
                 [:div.w-full.mb-3.text-sm
                  [select-vehicle-customer compound-config
                   {:on-value #(forms/add-many! ?used-vehicles-with-customers {'?customer-name %})}]]]]]
              [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
               [:div
                [ui/with-label
                 [:span.text-xs (tr "Number of used vehicles")]
                 [forms.ui/input-text ?used-vehicles
                  {:type "number" :min 0 :max 12}]]]])])

         (when-not (:booking/skip-new-vehicles? compound-config)
           [:<>
            [:div.flex.items-center.mb-3
             [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
              (tr "New vehicles")]
             [:div.border-b.flex-auto {:style {:height 1}}]]

            (cond
              (:booking/new-vehicle-make-required? compound-config)
              [:<>
             ;; show existing new vehicle entries
               (into [:<>]
                     (for [?vehicle ?new-vehicles-with-make]
                       [new-vehicle-form-with-make ?vehicle]))

             ;; add new vehicle by make
               [:div.md:grid.md:grid-cols-2.gap-3.mb-3
                [ui/with-label
                 [:span.text-xs (tr "Make")]
                 [:div.w-full.mb-3.text-sm
                  [select-vehicle-make {:on-value #(forms/add-many! ?new-vehicles-with-make {'?make-name %})}]]]]]

              (:booking/new-vehicle-customer-required? compound-config)
              [:<>
             ;; show existing new vehicle entries
               (into [:<>]
                     (for [?vehicle ?new-vehicles-with-customer]
                       [new-vehicle-form-with-customer compound-config ?vehicle]))

             ;; add new vehicle by make
               [:div.md:grid.md:grid-cols-2.gap-3.mb-3
                [ui/with-label
                 [:span.text-xs (tr "Customer")]
                 [:div.w-full.mb-3.text-sm
                  [select-vehicle-customer compound-config
                   {:on-value #(forms/add-many! ?new-vehicles-with-customer {'?customer-name %})}]]]]]
              :else
              [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
               [:div
                [ui/with-label
                 [:span.text-xs (tr "Number of new vehicles")]
                 [forms.ui/input-text ?new-vehicles
                  {:type "number" :min 0 :max 12}]]]])])]]
       (into [:div.border-t.py-2.px-6] (map forms.ui/view-message) (forms/visible-messages !form))
       [:div.pb-4.px-6.md:flex.justify-between.items-center
        [ui/button {:variant :white
                    :size 3
                    :on-click previous} "← " (tr "Back to compound selection")]
        [ui/button {:variant :primary
                    :size 3
                    :class "flex-shrink-0"}
         (tr "Book slot")]]])))

(defn receptionist-booking-form [{:keys [slots unloadings compound compound-config companies]}]
  (let [current-unloadings (->> unloadings
                                (map :unloading/scheduled-datetime)
                                (frequencies))
        bookable-slots (->> slots
                            first
                            second)
        add-new-company? (deframe/!route-get ::add-new-company?)]
    (with-form [!form {:unloading/company-name ?company-name
                       :new-company/name ?new-company-name
                       :new-company/vat-id ?new-company-vat-id
                       :booking/time ?booking-time
                       :transport/driver-name ?driver-name
                       :transport/driver-phone ?driver-phone-number
                       :transport/license-plate ?transport-plate
                       :transport/number ?transport-number
                       :transport/vehicles (vec (concat
                                                 (if (:booking/used-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?used-vehicles-with-customers :many {:vehicle/new? false
                                                                                          :vehicle/number (?vehicle-number :init 1)
                                                                                          :fahrzeug/customer {:customer/name ?customer-name}}))
                                                   (when ?used-vehicles
                                                     (take (js/parseInt ?used-vehicles)
                                                           (repeat {:vehicle/new? false}))))

                                                 (cond
                                                   (:booking/new-vehicle-make-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-make :many {:vehicle/new? true
                                                                                    :vehicle/number (?vehicle-number :init 1)
                                                                                    :vehicle/make {:make/name ?make-name}}))

                                                   (:booking/new-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-customer :many {:vehicle/new? true
                                                                                        :vehicle/number (?vehicle-number :init 1)
                                                                                        :fahrzeug/customer {:customer/name ?customer-name}}))

                                                   :else
                                                   (when ?new-vehicles
                                                     (take (js/parseInt ?new-vehicles)
                                                           (repeat {:vehicle/new? true}))))))}
                :required [?driver-name ?driver-phone-number ?transport-plate ?booking-time]
                :meta {'?new-company-vat-id {:validators [(fn [vat-id _]
                                                            (when (and (not (str/blank? vat-id))
                                                                       (not (contains? auth/vat-cc (apply str (take 2 vat-id)))))
                                                              {:type :invalid
                                                               :content (tr "Please use the international prefix: e.g. IT1234567")}))]}

                       '?driver-phone-number {:validators [(fn [phone _]
                                                             (when (and phone
                                                                        (not (str/blank? phone))
                                                                        (or (not (re-find phone-number-regex phone))
                                                                            (not (some (fn [p] (str/starts-with? phone p)) international-phone-prefixes))))
                                                               {:type :invalid
                                                                :content (str (tr "The phone number must be in international format: +XXXXX") "…")}))]}}
                :form/validators (conj unloading-validators
                                       (fn [form-data _]
                                         (when-not (or (:unloading/company-name form-data)
                                                       (and (:new-company/name form-data)
                                                            (:new-company/vat-id form-data)))
                                           {:type :invalid
                                            :content (tr "Specify a company or create a new one")})))]
      [:form.flex.flex-col.relative.bg-white.rounded-lg.max-w-3xl.mx-auto
       {:on-submit #(do (.preventDefault %)
                        (if (forms/submittable? !form)
                          (forms/watch-promise
                           !form
                           (p/let [{:keys [phone-number-valid?]} (http/$lambda 'ductile.sms/$validate {:number @?driver-phone-number})]
                             (if phone-number-valid?
                               (p/let [result (http/$lambda 'ductile.truck-booking/handle-schedule
                                                            {:compound-name compound
                                                             :unloading (assoc @!form
                                                                               :unloading/scheduled-duration (:duration (:booking/time @!form))
                                                                               :unloading/scheduled-datetime (:datetime (:booking/time @!form)))})]
                                 (if (:error-message result)
                                   (forms/message :error (:error-message result))
                                   (do (deframe/route-dissoc! :new-unloading)
                                       (deframe/route-refetch!)
                                       nil)))
                               (forms/message :error (tr "Phone number provided is not valid")))))
                          (forms/touch! !form)))}
       [:div.border-b.px-6.py-4
        [:h4.mt-0.text-base (tr "Add new unloading")]]
       [:div.flex-auto.px-6.pt-6.bg-slate-100
        [:div.md:flex.md:items-center
         {:class (if @add-new-company? "mb-2" "mb-3")}
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (if @add-new-company?
            [:div.inline
             (tr "Add a new company")
             [:span.font-normal.text-xs " " (tr "or") " "
              [:a.text-indigo-600.hover:underline.font-bold
               {:href "#" :on-click #(do (.preventDefault %)
                                         (.stopPropagation %)
                                         (deframe/route-update! ::add-new-company? not))}
               (tr "show me the list of existing companies instead")]]]
            (tr "Company"))]
         [:div.border-b.flex-auto.hidden.md:block {:style {:height 1}}]]
        (if @add-new-company?
          [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Company name")]
             [forms.ui/input-text ?new-company-name
              {:class "w-full"}]]]
           [:div
            [ui/with-label
             [:span.text-xs (tr "VAT ID")]
             [forms.ui/input-text ?new-company-vat-id
              {:class "w-full"}]]]]
          [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Select a company")]
             [forms.ui/combo-box ?company-name
              {:class "text-sm mr-2 w-full"
               :data-testid "company-select"
               :options (->> companies
                             (sort)
                             (map (fn [p] {:label p :value p})))}]]]
           [:div
            [:p.text-xs.mt-0.text-slate-600.mt-6.pt-1
             (tr "Couldn’t find a company in this list?")
             [:br]
             [:a.font-bold.text-indigo-600.hover:underline
              {:href "#" :on-click #(do (.preventDefault %)
                                        (.stopPropagation %)
                                        (deframe/route-update! ::add-new-company? not))}
              (tr "Add a new company")]]]])
        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Booking Time")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        [:div.py-6
         (into [:div.grid.grid-cols-3.md:grid-cols-9.gap-4]
               (for [slot bookable-slots
                     :let [full? (<= (:amount slot) (current-unloadings (:datetime slot)))]]
                 [:div.rounded.border-2.shadow.text-center.py-3.font-bold.cursor-pointer
                  {:class (cond
                            (= slot @?booking-time) "border-indigo-500 text-indigo-500"
                            full? "border-red-500 text-red-500"
                            :else "border-green-500 text-green-500")
                   :key (str slot)
                   :on-click #(reset! ?booking-time (when-not @?booking-time slot))
                   :style {:font-size 12}}
                  (format-slot-time {:break? true} (:datetime slot) [(:duration slot) :seconds])]))
         (into [:div.mt-1] (map forms.ui/view-message) (forms/visible-messages ?booking-time))]
        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Driver")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
         [:div
          [ui/with-label
           [:span.text-xs (tr "Name")]
           [forms.ui/input-text ?driver-name
            {:class "w-full"}]]]
         [:div
          [ui/with-label
           [:span.text-xs (tr "Mobile phone number")]
           [:div.flex
            [:div
             [forms.ui/input-text ?driver-phone-number
              {:placeholder (tr "+XXXXX")
               :class "w-full"}]]]]]]
        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Truck")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
         [:div
          [ui/with-label
           [:span.text-xs (tr "License plate")]
           [forms.ui/input-text ?transport-plate
            {:class "w-full"
             :data-testid "license-plate-input"}]]]
         [:div
          [ui/with-label
           [:span.text-xs (tr "Truck number") " (" (tr "optional") ")"]
           [forms.ui/input-text ?transport-number
            {:class "w-full"}]]]]

        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Used vehicles")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        (if (:booking/used-vehicle-customer-required? compound-config)
          [:<>
           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?used-vehicles-with-customers]
                   [used-vehicle-form-with-customer compound-config ?vehicle]))

           ;; add used vehicle by customer
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Customer")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-customer compound-config
               {:on-value #(forms/add-many! ?used-vehicles-with-customers {'?customer-name %})}]]]]]
          [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Number of used vehicles")]
             [forms.ui/input-text ?used-vehicles
              {:type "number" :min 0 :max 12}]]]])

        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "New vehicles")]
         [:div.border-b.flex-auto {:style {:height 1}}]]

        (cond
          (:booking/new-vehicle-make-required? compound-config)
          [:<>
           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?new-vehicles-with-make]
                   [new-vehicle-form-with-make ?vehicle]))

           ;; add new vehicle by make
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Make")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-make {:on-value #(forms/add-many! ?new-vehicles-with-make {'?make-name %})}]]]]]

          (:booking/new-vehicle-customer-required? compound-config)
          [:<>
           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?new-vehicles-with-customer]
                   [new-vehicle-form-with-customer compound-config ?vehicle]))

           ;; add new vehicle by make
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Customer")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-customer compound-config
               {:on-value #(forms/add-many! ?new-vehicles-with-customer {'?customer-name %})}]]]]]
          :else
          [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Number of new vehicles")]
             [forms.ui/input-text ?new-vehicles
              {:type "number" :min 0 :max 12}]]]])]

       (into [:div.border-t.py-2.px-6] (map forms.ui/view-message) (forms/visible-messages !form))
       [:div.pb-4.px-6.md:flex.justify-between.items-center
        [ui/button {:variant :primary
                    :size 4
                    :class "flex-shrink-0"}
         (tr "Add booking")]]])))

(defn confirmation [previous done]
  (let [submitting? @(deframe/!route-get :submitting?)
        compound-name @(deframe/!route-get :compound-name)
        {:as unloading
         :unloading/keys [scheduled-datetime scheduled-duration]
         :transport/keys [license-plate number vehicles]} @(deframe/!route-get :unloading)
        used-vehicles (remove :vehicle/new? vehicles)
        new-vehicles (filter :vehicle/new? vehicles)]
    [:div.bg-white.md:rounded-lg.max-w-3xl.mx-auto.mt-4.md:my-6.shadow-lg
     [:div
      [:div.px-6.py-4.border-b
       [:span.text-sm.text-slate-600.font-bold.mr-3.leading-none.flex-shrink-0.mt-0 (tr "Confirm your booking")]
       [:h4.mt-0.text-indigo-600 (format-slot-time {:date? true} scheduled-datetime [scheduled-duration :seconds])]]
      [:div.bg-slate-50.px-6.py-4
       [:div.flex.items-center.mb-3
        [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
         (tr "Compound")]]
       [:div.text-sm.mb-6 compound-name]
       [:div.flex.items-center.mb-3
        [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
         (tr "Driver")]]
       [:div.text-sm.mb-6 (str (:transport/driver-name unloading) " (" (:transport/driver-phone unloading) ")")]
       [:div.flex.items-center.mb-3
        [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
         (tr "License plate") " / " (tr "Truck number")]]
       [:div.text-sm.mb-6 (str license-plate (when number (str " / " number)))]
       [:div.flex.items-center.mb-3
        [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
         (tr "Load")]]
       [:ul.list-disc.text-sm.mb-6.ml-6
        (into [:<>]
              (for [[customer-name vehicles] (group-by (comp :customer/name :fahrzeug/customer) used-vehicles)]
                [:li.my-1 (str (trn ["%1 used vehicle" "%1 used vehicles"] (count vehicles)) (when customer-name (str " (" customer-name ")")))]))
        (into [:<>]
              (for [[grouping vehicles] (group-by #(or (-> % :vehicle/make :make/name)
                                                       (-> % :fahrzeug/customer :customer/name)) new-vehicles)]
                [:li.my-1 (str (trn ["%1 new vehicle" "%1 new vehicles"] (count vehicles)) (when grouping (str " (" grouping ")")))]))]
       [:div.flex.items-center.mb-3
        [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
         (tr "Additional instructions")]
        [:div.border-b.flex-auto {:style {:height 1}}]]
       [:p.text-sm
        (tr "In order to be able to use the slot you booked, you must show up at least 30 minutes in advance. Failure to do so might results in delays for the drivers and losing priority in the unloading queue.")]]]
     [:div.py-4.px-6.border-t.flex.flex-col-reverse.md:flex-row.justify-between.items-center
      [ui/button {:variant :white
                  :size 3
                  :on-click previous
                  :class "mt-4 md:mt-0"} "← " (tr "Back to slot selection")]
      [ui/button
       {:variant :primary
        :size 3
        :class "flex-shrink-0"
        :on-click done
        :disabled submitting?}
       [:span {:style {:color (when submitting? "transparent")}} (tr "Confirm booking")]
       [ui/spinner {:size 16 :class (str "mt-1 absolute " (if submitting? "block" "hidden"))}]]]]))

(defn summary [compound-name]
  (let [{:as unloading
         :unloading/keys [scheduled-datetime scheduled-duration]
         :transport/keys [license-plate number]} @(deframe/!route-get :unloading)]
    [:div.bg-white.md:rounded-lg.w-full.max-w-lg.mx-auto.md:my-12.shadow-lg
     [:div
      [:div.md:flex.md:items-center.px-6.py-4.border-b.text-center.md:text-left
       [:svg.text-indigo-600.mb-4.md:mb-0.mx-auto.md:mx-0
        {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke "currentColor"
         :class "w-12 h-12 md:w-[80px] md:h-[80px]"}
        [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2" :d "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"}]]
       [:div.ml-2
        [:div.mb-2.text-xs.font-bold.text-indigo-600.md:leading-none.flex-shrink-0.mt-0.mx-6.md:mx-0 (tr "You successfully booked your unloading slot for:")]
        [:h4.text-lg.mt-0.mb-2.md:leading-none.text-indigo-600 compound-name]
        [:h4.text-lg.mt-0.mb-2.md:leading-none.text-indigo-600 (format-slot-time {:date? true} scheduled-datetime [scheduled-duration :seconds])]
        [:div.font-bold.text-xs.text-indigo-600.md:leading-none.flex-shrink-0 {:style {:padding-top 2}} (tr "Booking ID") ": " (:ductile/code unloading)]]]
      [:div.px-7.py-4.bg-slate-50
       [:div.mb-3
        [:span.text-slate-500.block.text-xs.uppercase.tracking-wider.mb-1
         (tr "Driver")]
        [:span (str (:transport/driver-name unloading) " (" (:transport/driver-phone unloading) ")")]]
       [:div
        [:span.text-slate-500.block.text-xs.uppercase.tracking-wider.mb-1
         (tr "License plate") " / " (tr "Truck number") ": "]
        [:span (str license-plate (when number (str " / " number)))]]]]
     [:div.py-4.px-7.border-t.md:flex.justify-end.items-center
      [ui/button
       {:variant :primary
        :size 3
        :class "flex-shrink-0"
        :on-click #(router/navigate-to (router/path '[unloadings bookings]))}
       (tr "Go back home")]]]))

(defn load-promise!
  "Wraps promise to store :loading? and :error route state.
   Promise will return a map containing :value or :error."
  [promise]
  (deframe/route-assoc! :loading? true)
  (p/try (p/let [result promise]
           (deframe/route-dissoc! :loading?)
           {:value result})
         (p/catch js/Error e
           (deframe/route-assoc! :loading? false)
           (deframe/route-assoc! :error e)
           {:error e})))

(defn request-slots [compound-name]
  (http/$lambda 'ductile.truck-booking/handle-fetch-slots
                {:compound-name compound-name}))

(defn select-compound [compounds done]
  (r/with-let [!selected-compound (r/atom "")]
    [:div.flex.flex-col.relative.bg-white.md:rounded-lg.max-w-3xl.mx-auto.mt-4.md:my-6.shadow-lg
     [:div.flex.justify-between.items-center.py-4.px-6.border-b
      [:h4.mt-0.text-base.font-bold (tr "Select compound")]]
     [:div.bg-slate-50
      [:div.flex-auto.px-6.py-4
       [ui/select {:value @!selected-compound
                   :data-testid "truck-booking-compound-select"
                   :on-change #(reset! !selected-compound %)
                   :placeholder (tr "Select compound")
                   :choices (map (fn [compound]
                                   {:ui/value compound :ui/label compound})
                                 (sort compounds))
                   :size 3}]]]
     [:div.border-t.py-2.px-6]
     [:div.pb-4.px-6.md:flex.justify-between.items-center
      [ui/button {:variant :primary
                  :size 3
                  :class "flex-shrink-0"
                  :disabled (str/blank? @!selected-compound)
                  :on-click #(done @!selected-compound)}
       (tr "Add transport details")]]]))

(defn booking [compounds]
  (let [step @(deframe/!route-get :step)
        loading? @(deframe/!route-get :loading?)
        error @(deframe/!route-get :error)
        unloading @(deframe/!route-get :unloading)
        compound-name @(deframe/!route-get :compound-name)]
    [:div.flex.flex-col
     ;; TODO - style appropriately
     (when loading? "Loading")
     [ui/top-bar
      [:h1.font-bold.text-lg (tr "Book unload slot")]]
     [:div.relative.bg-slate-100
      (when error
        [:div.bg-white.md:rounded-lg.max-w-3xl.mx-auto.mt-4.md:my-6.shadow-lg.justify-center
         [:div.mb-8
          [ui/flash-notice {:type :error :message [:div
                                                   [:div.font-bold (tr "There was an error with your submission")]
                                                   [:div.mt-2
                                                    [:ul.list-disc.pl-5.space-y-1
                                                     [:li (str error)]]]]}]]])
      (when-not (= :summary step)
        [:div.mx-auto
         {:class "px-6 max-w-3xl mb-6"}
         [steps [{:label (tr "Select compound") :active? (= :select-compound step)}
                 {:label (tr "Transport details") :active? (= :transport-details step)}
                 {:label (tr "Select slot") :active? (= :select-slot step)}
                 {:label (tr "Confirmation") :active? (= :confirmation step)}]]])
      (case step
        :transport-details [transport-details (cc/get-compound-config compound-name)
                            #(deframe/route-assoc! :step :select-compound)
                            (fn [unloading]
                              (p/let [{slots :value :keys [error]} (load-promise! (request-slots compound-name))]
                                (when-not error
                                  (deframe/route-assoc! :slots slots)
                                  (deframe/route-assoc! :unloading unloading)
                                  (deframe/route-assoc! :step :select-slot))))]
        :select-compound [select-compound compounds #(do (deframe/route-assoc! :compound-name %)
                                                         (deframe/route-assoc! :step :transport-details))]
        :select-slot [select-slot compound-name
                      #(deframe/route-assoc! :step :transport-details)
                      #(do (deframe/route-assoc! :step :confirmation)
                           (deframe/route-dissoc! :error)
                           (deframe/route-update! :unloading (fn [x] (merge x %))))]
        :confirmation [confirmation
                       #(do (deframe/route-assoc! :step :select-slot)
                            (deframe/route-dissoc! :error))
                       (fn []
                         (deframe/route-assoc! :submitting? true)
                         (p/let [result (http/$lambda 'ductile.truck-booking/handle-schedule
                                                      {:compound-name compound-name
                                                       :unloading unloading})]

                           (deframe/route-dissoc! :submitting?)
                           (if (:error-message result)
                             (deframe/route-assoc! :error (:error-message result))
                             (do (deframe/route-assoc! :step :summary)
                                 (deframe/route-assoc-in! [:unloading :ductile/code] (:ductile/code result))))))]
        :summary [summary compound-name]

        ;; default
        [select-compound compounds #(do (deframe/route-assoc! :compound-name %)
                                        (deframe/route-assoc! :step :transport-details))])]]))

(defn reservation-choice [unloading]
  [:li.px-4.py-3.border-t.border-slate-200.first:border-0
   [:div.text-sm
    [:span.font-bold
     (format-slot-time {:date? true} (:scheduled-datetime unloading) [(:scheduled-duration unloading) :seconds])]]
   [:div.mt-1.text-xs
    [:span.font-bold [:span.text-slate-500 (tr "Driver") ": "] (:driver-name unloading)]
    [:span.text-slate-500.ml-3 (tr "License plate") ": "] (:transport-license-plate unloading)]])

(defn manage-unloading-status [{:keys [compound-config compound now unloadings] :as _props}]
  (let [selected-unloading @(deframe/!route-get :selected-unloading)]
    [:div.bg-slate-100.flex.h-full.relative
     [:div.flex-auto.overflow-y-auto.my-6
      [:div.flex-auto.w-full.items-center
       (into [:div.grid.grid-cols-2.md:grid-cols-4.gap-6.px-6.pb-6.mx-auto
              {:style {:max-width 790}}]
             (for [i (range 1 (inc (:compound/unloading-lines compound-config)))
                   :let [unloading (first (filter #(= i (:unloading/line %))
                                                  unloadings))]]
               [:div.rounded-lg.border-2.bg-white.shadow.text-xs.font-medium.cursor-pointer.items-center.flex.items-center.justify-center.relative.hover:ring-indigo-200.hover:ring-2.hover:ring-offset-4.p-3
                {:style {:height 124}
                 :on-click #(deframe/route-assoc! :selected-unloading unloading)
                 :class (str (cond
                               (not unloading) "border border-slate-300 bg-slate-50 text-slate-500 uppercase tracking-wider"
                               #_#_(:unloading/start-datetime unloading) "border-red-500 bg-red-50 text-red-700"
                               :else "border-2 bg-orange-100 text-orange-700 border-orange-400"))}
                [:div.absolute.left-0.top-0.rounded-br-md.rounded-tl
                 {:style {:padding "1px 6px 2px 4px" :font-size 10}
                  :class (cond
                           (not unloading) "bg-slate-300 text-white"
                           #_#_(:unloading/start-datetime unloading) "bg-red-500 text-white"
                           :else "bg-orange-400 text-white")}
                 [:span.font-bold i]]
                [:div.text-center
                 [:div
                  (when unloading
                    [:div.tracking-wide.uppercase.mb-1.absolute.font-bold
                     {:style {:font-size 10 :line-height 1.3 :right 4 :top 5}}
                     (if (:unloading/start-datetime unloading) (tr "Unloading") (tr "Assigned"))])
                  (if unloading
                    [:div
                     {:style {:font-size 12 :line-height 1.3}}
                     [:div (:transport/driver-name unloading)]
                     [:div (get-in unloading [:unloading/company :company/name])]
                     [:div (:transport/license-plate unloading)]]
                    [:div {:style {:font-size 11}} (tr "Unused")])
                  (when (:unloading/start-datetime unloading)
                    [:div.absolute.right-0.bottom-0.rounded-tl-md.rounded-br.leading-none
                     {:style {:padding "3px 3px 1px 4px" :font-size 10}
                      :class "bg-orange-400 text-white"}
                     [:span.font-bold (minutes+seconds-between now (:unloading/start-datetime unloading))]])]]]))]]
     [:div.absolute.left-0.top-0.w-full.h-full.bg-gray-500.bg-opacity-25.transition-opacity
      {:on-click #(deframe/route-dissoc! :selected-unloading)
       :class (if selected-unloading "opacity-100" "opacity-0 pointer-events-none")}]
     [:<>
      (let [{:unloading/keys [start-datetime arrival-datetime]
             :transport/keys [license-plate number vehicles]} selected-unloading
            used-vehicles (remove :vehicle/new? vehicles)
            new-vehicles (filter :vehicle/new? vehicles)]
        [:div.absolute.right-0.top-0.h-full.bg-white.shadow-xl.transition-all.duration-150
         {:style {:width 300}
          :class (if selected-unloading "translate-x-0 opacity-100" "translate-x-full pointer-events-none opacity-0")}
         (when selected-unloading
           [:div.h-full.flex.flex-col
            [:button.top-2.right-2.absolute.bg-white.rounded-md.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-offset-2.focus:ring-indigo-500
             {:on-click #(deframe/route-dissoc! :selected-unloading)
              :type "button"}
             [:span.sr-only "Close panel"]
             [:svg.h-6.w-6 {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke "currentColor" :aria-hidden "true"}
              [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2" :d "M6 18L18 6M6 6l12 12"}]]]
            [:div.px-4.py-3.flex-auto.overflow-y-scroll
             [:div.mb-2
              [:div.text-lg (:transport/driver-name selected-unloading)]
              [:div.text-sm.text-gray-500 (:transport/driver-phone selected-unloading)]
              [:div.text-sm.text-gray-500 (get-in selected-unloading [:unloading/company :company/name])]]
             [:div.border-b.flex-auto {:style {:height 1}}]
             [:ul.text-xs.mt-3.mb-4
              [:li.font-bold
               [:span.mr-2 (tr "Arrived at") ": "]
               [:span (time-format/->str {:format :time} arrival-datetime)]]
              [:li.font-bold
               [:span.mr-2 (tr "Entered at") ": "]
               [:span (if start-datetime (time-format/->str {:format :time} start-datetime) "–")]]]
             [:div.flex.items-center.mb-2
              [:h4.text-xs.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
               (tr "License plate") " / " (tr "Truck number")]
              [:div.border-b.flex-auto {:style {:height 1}}]]
             [:div.text-xs.mb-6 (str license-plate (when number (str " / " number)))]
             [:div.flex.items-center.mb-2
              [:h4.text-xs.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
               (tr "Load")]
              [:div.border-b.flex-auto {:style {:height 1}}]]
             [:ul.list-disc.text-xs.ml-6.mb-3
              [:li.my-1 (trn ["%1 used vehicle" "%1 used vehicles"] (count used-vehicles))]
              [:<>
               (for [[make-name vehicles] (group-by (comp :make/name :vehicle/make) new-vehicles)]
                 [:li.my-1 (str (trn ["%1 new vehicle" "%1 new vehicles"] (count vehicles)) " (" make-name ")")])]]]
            [:div.bg-white.border-t.p-3.flex
             [ui/button
              {:variant :primary
               :size 3
               :on-click #(.then (http/$lambda (if start-datetime
                                                 'ductile.truck-booking/handle-finished
                                                 'ductile.truck-booking/handle-started)
                                               {:unloading-id (:ductile/id selected-unloading)})
                                 (fn [_]
                                   (deframe/route-dissoc! :selected-unloading)
                                   (deframe/route-refetch!)))
               :class "flex-auto"}
              (if start-datetime
                (tr "Finish unloading")
                (tr "Start unloading"))]]])])]]))

(defn mark-arrived-form []
  (let [current-unloading @(deframe/!route-get :current-unloading)
        {:unloading/keys [scheduled-datetime scheduled-duration]} current-unloading]
    (with-form [!form {:transport/driver-name (?driver-name :init (:transport/driver-name current-unloading))
                       :transport/driver-phone (?driver-phone :init (:transport/driver-phone current-unloading))}
                :required [?driver-name ?driver-phone]]
      [:form.bg-slate-100
       {:on-submit #(do (.preventDefault %)
                        (if (forms/submittable? !form)
                          (forms/watch-promise
                           !form
                           (p/let [result (http/$lambda 'ductile.truck-booking/handle-arrived
                                                        {:unloading (assoc @!form :ductile/id (:ductile/id current-unloading))})]
                             (if (:error-message result)
                               (forms/message :error (:error-message result))
                               (do (deframe/route-dissoc! :current-unloading)
                                   (deframe/route-refetch!)
                                   nil))))
                          (forms/touch! !form)))}
       [:div.mb-4.pb-4.border-b.px-8.py-6.bg-white
        [:h4.text-sm.mt-0.text-slate-600 (tr "Booking ID") ": " (:ductile/code current-unloading)]
        [:h4.mt-0.text-slate-600 (format-slot-time {:date? true} scheduled-datetime [scheduled-duration :seconds])]]
       [:div.md:grid.md:grid-cols-2.gap-4.px-8
        [ui/with-label
         [:span.text-xs (tr "Driver name")]
         [forms.ui/input-text ?driver-name
          {:class "w-full"}]]
        [ui/with-label
         [:span.text-xs (tr "Driver Phone")]
         [forms.ui/input-text ?driver-phone
          {:class "w-full"}]]]
       (when (seq (forms/visible-messages !form))
         (into [:div.px-8.py-2] (map forms.ui/view-message) (forms/visible-messages !form)))
       [:div.flex.px-8.py-6
        {:class (when-not (forms/valid? !form) "mt-4")}
        [ui/button
         {:variant :primary
          :size 3
          :class "flex-auto"}
         (tr "Save and mark truck as arrived")]]])))

(defn reservations-search [{:keys [unloadings]}]
  [:div.flex-auto.md:mr-3.mb-3.md:mb-0
   [combobox/view {:placeholder (tr "Search reservations: Booking ID, driver, license plate")
                   :choices (mapv (fn [u] {:ui/label (str/join " " [(:transport/driver-name u)
                                                                    (:transport/license-plate u)
                                                                    (:ductile/code u)])
                                           :ui/value {:ductile-id (:ductile/id u)
                                                      :ductile-code (:ductile/code u)
                                                      :scheduled-datetime (:unloading/scheduled-datetime u)
                                                      :arrival-datetime (:unloading/arrival-datetime u)
                                                      :scheduled-duration (:unloading/scheduled-duration u)
                                                      :transport-license-plate (:transport/license-plate u)
                                                      :driver-name (:transport/driver-name u)}})
                                  unloadings)
                   :clear-on-change? true
                   :on-change (fn [{unloading :ui/value}]
                                (deframe/route-assoc! :current-unloading (first (filter #(= (:ductile/id %)
                                                                                            (:ductile-id unloading))
                                                                                        unloadings))))
                   :choice-view reservation-choice
                   :left [:div.flex.items-center.justify-center.h-full.pl-2
                          [:svg.h-5.w-5
                           {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke "currentColor" :style {:top "0.3rem" :left 10}}
                           [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2" :d "M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"}]]]}]])

(defn receptionist-options []
  [:<>
   [ui/button {:variant :white
               :on-click #(router/navigate-to (router/path '[unloadings :compound-name slots-overview] {:compound-name (util/current-compound-name)}))}
    [ui/icon {:size 18} :calendar]
    (tr "Opening hours")]
   [ui/button {:variant :primary
               :on-click #(deframe/route-assoc! :new-unloading {})}
    [ui/icon {:size 18} :plus]
    (tr "Unloading")]

   #_[:button.bg-indigo-600.font-bold.text-sm.text-white.rounded.pl-2.pr-3.py-1.md:mr-4.flex.items-center.flex-shrink-0.shadow.border.border-indigo-700.hover:bg-indigo-700.transition-all
      {:on-click #(swap! !state assoc :new-unloading {})}
      [ui/icon {:size 18} :plus]
      [:span.ml-2 (tr "Unloading")]]])

(defn receptionist-overview [{:keys [compound-config slots unloadings waiting-queue-unloadings now] :as props}]
  (let [new-unloading @(deframe/!route-get :new-unloading)
        current-unloading @(deframe/!route-get :current-unloading)]
    [:div.bg-slate-100.flex.flex-col.relative
     {:class "min-h-screen"}
     (when new-unloading
       [ui/modal {:on-close #(deframe/route-dissoc! :new-unloading)
                  :attrs {:key "_" ;; fix reagent unique key warning
                          :class "w-[800px]"}}
        [receptionist-booking-form props]])
     (when current-unloading
       [ui/modal {:on-close #(deframe/route-dissoc! :current-unloading)}
        [mark-arrived-form]])
     [:<>
      [ui/nav-bar]
      [:div.bg-slate-50.pl-4.pr-2.py-2.border-b.shadow.hidden.md:block
       [:div.md:flex.md:flex-row.md:flex-auto.md:items-center.gap-2
        [reservations-search props]
        [receptionist-options]]]]
     [:div.flex-auto.relative.w-full
      [:div
       {:class "md:mr-[200px]"}
       (into
        [:div.grid.gap-3.grid-cols-2.md:grid-cols-10.p-5.bg-slate-100]
        (let [current-unloadings (filter (fn [u] (and (not (:unloading/end-datetime u))
                                                      (:unloading/line u)))
                                         unloadings)]
          (for [i (range 1 (inc (:compound/unloading-lines compound-config)))
                :let [unloading (first (filter #(= i (:unloading/line %))
                                               current-unloadings))]]
            [:div.rounded-lg.bg-white.shadow.text-xs.font-medium.cursor-pointer.items-center.flex.items-center.justify-center.relative.hover:ring-indigo-200.hover:ring-2.hover:ring-offset-4.p-6
             {:style {:height 90}
              :class (str (cond
                            (not unloading) "border border-slate-300 bg-slate-50 text-slate-500 uppercase tracking-wider"
                            #_#_(:unloading/start-datetime unloading) "border-red-500 bg-red-50 text-red-700"
                            :else "border-2 bg-orange-100 text-orange-700 border-orange-400"))}
             [:div.absolute.left-0.top-0.rounded-br-md.rounded-tl
              {:style {:padding "1px 6px 2px 4px" :font-size 10}
               :class (cond
                        (not unloading) "bg-slate-300 text-white"
                        #_#_(:unloading/start-datetime unloading) "bg-red-500 text-white"
                        :else "bg-orange-400 text-white")}
              [:span.font-bold i]]
             [:div.text-center
              [:div
               (when unloading
                 [:div.tracking-wide.uppercase.mb-1.absolute.font-bold
                  {:style {:font-size 8 :line-height 1.3 :right 4 :top 5}}
                  (if (:unloading/start-datetime unloading) (tr "Unloading") (tr "Assigned"))])
               (if unloading
                 [:div
                  {:style {:font-size 10 :line-height 1.3}}
                  [:div (:transport/driver-name unloading)]
                  [:div (:transport/license-plate unloading)]]
                 [:div {:style {:font-size 11}} (tr "Unused")])
               (when (:unloading/start-datetime unloading)
                 [:div.absolute.right-0.bottom-0.rounded-tl-md.rounded-br.leading-none
                  {:style {:padding "3px 3px 1px 4px" :font-size 10}
                   :class "bg-orange-400 text-white"}
                  [:span.font-bold (minutes+seconds-between now (:unloading/start-datetime unloading))]])]]])))
       (into [:<>]
             (for [[date-str s] slots
                   :let [date (js/Date. date-str)]]
               [:div.bg-slate-100.overflow-x-auto.p-4
                {:key date-str}
                [:div.bg-gray-50.p-4.border-gray-200.border-t.border-x.rounded-t
                 [:h2.text-base.font-bold.leading-none (time-format/->str {:format :date :style :full} date)]]
                [:table.min-w-full.border-t.border-l
                 (into [:tbody]
                       (for [{:keys [datetime duration amount]} (sort-by :datetime s)
                             :let [unloadings (filter (fn [{:unloading/keys [scheduled-datetime]}] (= datetime scheduled-datetime))
                                                      unloadings)]]
                         [:tr.border-b.align-center.odd:bg-slate-50.border-r
                          [:th.text-left.pl-5.pr-3.py-2.border-r
                           {:style {:font-size 12}}
                           (format-slot-time {} datetime [duration :seconds])]
                          (into [:<>]
                                (for [u (sort-by :transport/driver-name unloadings)]
                                  [:td.pl-3.pr-3.py-2.border-r.hover:bg-slate-300.cursor-pointer
                                   {:style {:font-size 12}
                                    :on-click (when-not (:unloading/arrival-datetime u)
                                                #(deframe/route-assoc! :current-unloading u))}
                                   [:span.whitespace-nowrap (:transport/driver-name u)]
                                   [:br]
                                   [:span.text-slate-500.tabular-nums (:transport/license-plate u)]
                                   [:br]
                                   [:span.uppercase.tracking-wide
                                    {:style {:font-size 10}
                                     :class (cond
                                              (:unloading/end-datetime u) "text-slate-500"
                                              (:unloading/line u) "text-orange-700"
                                              (:unloading/arrival-datetime u) "text-blue-500"
                                              :else "")}
                                    (cond
                                      (:driver/unreachable? u) (tr "Unreachable")
                                      (:unloading/end-datetime u) (tr "Done")
                                      (:unloading/start-datetime u) (str (tr "Unloading") " " (tr "at") " " (:unloading/line u))
                                      (:unloading/line u) (str (tr "Assigned") " " (tr "at") " " (:unloading/line u))
                                      (:unloading/arrival-datetime u) (tr "Queued")
                                      :else "")]]))
                          (into [:<>]
                                (repeat (- amount (count unloadings))
                                        [:td.px-3.py-2.border-r.hover:bg-slate-300.cursor-pointer.bg-green-50
                                         {:style {:font-size 12 :min-width "100px"}}
                                         [:span.text-green-600.uppercase.tracking-wide.font-bold (tr "Free")]]))]))]]))]
      [:div.md:bg-slate-50.md:border-slate-200.md:border-l.md:absolute.right-0.top-0.bottom-0.md:overflow-y-auto.md:shadow-lg
       {:class "md:w-[200px]"}
       [:div.mb-3
        (let [late-unloadings (filter (fn [u] (and (before? (:unloading/scheduled-datetime u) now)
                                                   (not (:unloading/arrival-datetime u))))
                                      unloadings)]
          (into
           [:<>
            [:div.font-bold.px-4.py-2.text-xs (tr "Late") " (" (count late-unloadings) ")"]]
           (for [u late-unloadings]
             [:div.hover:bg-slate-300.px-4.py-1.cursor-pointer.mb-1
              {:on-click #(deframe/route-assoc! :current-unloading u)
               :style {:font-size 12}}
              [:span (:transport/driver-name u)]
              [:span.text-slate-500 " (" (.getHours (:unloading/scheduled-datetime u)) ":00)"]
              [:br]
              [:span.text-slate-500.tabular-nums (:transport/license-plate u)]])))]
       [:div
        (let [unreachable-unloadings (filter :driver/unreachable? unloadings)]
          (into
           [:<>
            [:div.font-bold.px-4.py-2.text-xs (tr "Unreachable") " (" (count unreachable-unloadings) ")"]]
           (for [u unreachable-unloadings]
             [:div.hover:bg-slate-300.px-4.py-1.cursor-pointer.mb-1
              {:on-click #(deframe/route-assoc! :current-unloading u)
               :style {:font-size 12}}
              [:span (:transport/driver-name u)]
              [:span.text-slate-500 " (" (.getHours (:unloading/scheduled-datetime u)) ":00)"]
              [:br]
              [:span.text-slate-500.tabular-nums (:transport/license-plate u)]])))]
       [:div.text-xs
        (into
         [:<>
          [:div.font-bold.px-4.py-2.text-xs (tr "Queue") " (" (count waiting-queue-unloadings) ")"]]
         (for [u waiting-queue-unloadings]
           [:div.group.hover:bg-slate-300.px-4.py-1.cursor-pointer.mb-1
            {:style {:font-size 12}}
            [:span.flex-auto (:transport/driver-name u)] [:br]
            [:span.text-slate-500.tabular-nums (:transport/license-plate u)]]))]]]]))

(defn bookings-overview [{:keys [unloadings]}]
  (r/with-let [filter-value (r/atom (tr "Scheduled"))]
    (let [recent? (fn [u] (after? (:unloading/scheduled-datetime u)
                                  (minus (js/Date.) [12 :hours])))
          late? (fn [u] (between? (minus (js/Date.) [12 :hours])
                                  (plus (:unloading/scheduled-datetime u) [1 :hours])
                                  (js/Date.)))
          no-show-or-not-processed (fn [u] (and (not (recent? u))
                                                (not (:unloading/end-datetime u))))
          scheduled (fn [u] (and (recent? u)
                                 (not (:unloading/end-datetime u))
                                 (not (:unloading/arrival-datetime u))))
          at-compound (fn [u] (and (not (:unloading/end-datetime u))
                                   (:unloading/arrival-datetime u)))
          completed (fn [u] (some? (:unloading/end-datetime u)))
          filtered-unloadings (cond (= (tr "Scheduled") @filter-value)
                                    (filter scheduled unloadings)
                                    (= (tr "At compound") @filter-value)
                                    (filter at-compound unloadings)
                                    (= (tr "Completed") @filter-value)
                                    (filter completed unloadings)
                                    (= (tr "No show / Not handled") @filter-value)
                                    (filter no-show-or-not-processed unloadings)
                                    :else
                                    unloadings)]
      [:div.bg-slate-100.min-h-screen
       [ui/nav-bar]
       (if (seq unloadings)
         [:div
          [:div.flex.md:items-center.justify-between.px-6.pt-6
           [:h4.my-2.font-bold.text-lg (tr "Your booked unloadings")]
           [:div.md:flex.md:items-center.md:flex-wrap
            [ui/button
             {:variant :primary
              :size 4
              :class "items-center flex mb-4 w-full sm:w-auto"
              :label "unloadings-bookings-book-unloading"
              :on-click #(router/navigate-to (router/path '[unloadings book]))}
             [ui/icon {:size 18} :plus]
             [:span.ml-2 (tr "Book unloading")]]
            [:div.relative.mb-4.ml-3
             [combobox/view {:value (combobox/->choice @filter-value)
                             :on-change #(reset! filter-value (:ui/value %))
                             :choices (map combobox/->choice [(tr "Scheduled") (tr "At compound") (tr "Completed") (tr "No show / Not handled") (tr "All unloadings")])
                             :size 4}]]]]

          [:div.sm:px-6.sm:pb-6
           [:table.min-w-full.divide-y.divide-slate-200.shadow.md:overflow-x-auto.border-b.border-slate-200.sm:rounded-lg.text-sm
            [:thead.bg-slate-50
             [:tr
              [:th.px-6.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Transport details")]
              [:th.px-6.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Scheduled at")]
              [:th.px-6.py-3.text-center.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Load")]
              [:th.px-6.py-3.text-right.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Progress")]]]
            (if (seq filtered-unloadings)
              (into
               [:tbody.bg-white.divide-y.divide-slate-200]
               (map (fn [{:ductile/keys [code location] :unloading/keys [scheduled-datetime start-datetime arrival-datetime scheduled-duration end-datetime]
                          :transport/keys [driver-name license-plate vehicles] :as u}]
                      (let [cc (cc/get-compound-config (:compound/name location))
                            used (remove :vehicle/new? vehicles)
                            new (filter :vehicle/new? vehicles)]
                        [:tr {:class (when end-datetime "text-gray-500 bg-slate-50")}
                         [:td.px-6.py-4 {:class "w-1/4"}
                          [:div [:span.tabular-nums.font-bold license-plate] " (" driver-name ")"]
                          [:div [:small "ID: " code]]]
                         [:td.px-6.py-4.text-left {:class "w-1/4"}
                          [:div.my-2
                           (time-format/->str {:format :date :zone (:compound/timezone cc)} scheduled-datetime)
                           ", "
                           (format-slot-time {:zone (:compound/timezone cc)} scheduled-datetime [scheduled-duration :seconds])
                           ", " (:compound/name location)]]
                         [:td.tabular-nums.px-6.py-4.text-center {:class "w-1/4"}
                          [:ul
                           (when (seq used)
                             [:li (tr "Used") ": " (str/join ", " (for [[customer-name vehicles] (group-by (comp :customer/name :fahrzeug/customer) used)]
                                                                    (str (count vehicles) (when customer-name (str " " customer-name)))))])
                           (when (seq new)
                             [:li (tr "New") ": " (str/join ", " (for [[make-name vehicles] (group-by (comp :make/name :vehicle/make) new)]
                                                                   (str (count vehicles) (when make-name (str " " make-name)))))])]]
                         [:td.px-6.py-4.text-right.text-xs {:class "w-1/4"}
                          (cond
                            (scheduled u)
                            [:div.flex.items-center.justify-end.flex-wrap
                             [ui/badge {:variant :green} (tr "Scheduled")]
                             (when (late? u)
                               [ui/badge {:variant :orange} (tr "Late")])]
                            (at-compound u)
                            [ui/badge {:variant :gray} (tr "At compound")]
                            (completed u)
                            [ui/badge {:variant :white-gray} (tr "Completed")]
                            (no-show-or-not-processed u)
                            [ui/badge {:variant :red} (tr "No show / Not handled")]
                            :else "")]]))
                    (sort-by (comp - :unloading/scheduled-datetime) filtered-unloadings)))
              [:tbody.bg-white.divide-y.divide-slate-200
               [:tr
                [:td.p-4.text-center.font-bold.text-gray-500.text-sm {:col-span 4}
                 (tr "No unloadings with the selected criteria.")]]])]]]
         [:div.max-w-2xl.mx-auto.p-6.md:pt-24
          [:div.text-center
           [:div.flex.justify-center.text-slate-500.mb-2
            [:svg.h-12.w-12 {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke "currentColor"}
             [:path {:d "M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"}]
             [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2" :d "M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"}]]]
           [:h4.m-0.text-slate-500 (tr "No unloadings")]
           [:p.mt-2.mb-6 (tr "Get started by booking an unloading.")]
           [ui/button
            {:variant :primary
             :size 3
             :class "flex items-center"
             :label "unloadings-bookings-book-unloading"
             :on-click #(router/navigate-to (router/path '[unloadings book]))}
            [ui/icon {:size 18} :plus]
            [:span.ml-2 (tr "Book unloading")]]]])])))

(defscene show-select-compound
  :params (atom {:step :select-compound
                 :compounds ["Piadena" "Riedstadt"]})
  [!state]
  [booking !state])

(defn ->iso-date [datetime]
  (.. datetime toISOString (substring 0 10)))

(defn demo-slots []
  (let [slot-data (apply merge
                         (for [d (range 1 8)
                               :let [day (plus (js/Date.) [d :days])
                                     day-name (nth ["sunday" "monday" "tuesday" "wednesday" "thursday" "friday" "saturday"] (.getDay day))
                                     [[opening-hour closing-hour] _slots] #_[[0 24] 3]
                                     (first
                                      (get-in (cc/get-compound-config "Piadena")
                                              [:compound/booking-slots+times day-name]))]]
                           (map (fn [h]
                                  {:datetime (at day (inc h))
                                   :duration 3600
                                   :amount (rand-int 5)})
                                (range opening-hour closing-hour))))]
    (->> slot-data
         (flatten)
         (group-by (comp ->iso-date :datetime))
         (sort-by first))))

(defscene slots
  :params (atom {:step :select-slot
                 :slots (demo-slots)})
  [!state]
  [booking !state])

(defscene slots-after-hours
  :params (atom {:step :select-slot
                 :slots (demo-slots)})
  [!state]
  [booking !state])

(defscene show-confirmation
  :params (atom {:step :confirmation
                 :compound {:compound/name "Piadena"}
                 :unloading {:unloading/scheduled-duration 3600
                             :unloading/scheduled-datetime #inst"2022-01-21T10:00:00.000-00:00"
                             :transport/license-plate "FF123LO"
                             :transport/number "GG123"
                             :transport/driver-name (random-driver-name)
                             :transport/driver-phone "+393333123456"
                             :transport/vehicles [{:vehicle/new? false}
                                                  {:vehicle/new? false}
                                                  {:vehicle/new? true :vehicle/make {:make/name "BMW"}}
                                                  {:vehicle/new? true :vehicle/make {:make/name "VW"}}]}})
  [!state]
  [booking !state])

(defscene show-summary
  :params (atom {:step :summary
                 :compound {:compound/name "Piadena"}
                 :unloading {:unloading/scheduled-duration 3600
                             :unloading/scheduled-datetime #inst"2022-01-21T10:00:00.000-00:00"
                             :unloading/code "ABAU123GG1"
                             :transport/license-plate "FF123LO"
                             :transport/number "GG123"
                             :transport/driver-name (random-driver-name)
                             :transport/driver-phone "+393333123456"}})
  [!state]
  [booking !state])


(def truck-booking-compounds
  (into []
        (keep (fn [[compound-name compound-config]]
                (when (-> compound-config :compound/modules (contains? :module/truck-booking))
                  compound-name)))
        cc/compound-config))

(defn render-booking [_result _match]
  [booking truck-booking-compounds])

(def compound-worker-demo-state
  {:now #inst"2022-01-21T10:45:00.000-00:00"
   :compound-config (cc/get-compound-config "Piadena")
   :compound {:compound/name "Piadena"
              :compound/truck-assignment-enabled? true}
   :unloadings [(random-unloading {:status :started :line 1})
                (random-unloading {:status :allowed-entry :line 2})
                (random-unloading {:status :allowed-entry :line 6})
                (random-unloading {:status :started :line 7})
                (random-unloading {:status :started :line 9})]})

(def receptionist-demo-state
  (let [now #inst"2022-01-21T12:45:00.000-00:00"
        cc (cc/get-compound-config "Piadena")
        unloadings (concat
                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 7)
                                                    :status :finished}))
                    [(random-unloading {:scheduled-at (at now 7)})]

                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 4)))
                                #(random-unloading {:scheduled-at (at now 8)
                                                    :status :finished}))
                    [(random-unloading {:scheduled-at (at now 8)})]
                    [(random-unloading {:scheduled-at (at now 8)})]

                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 9)
                                                    :status :finished}))
                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 10)
                                                    :status :finished}))
                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 11)
                                                    :status :finished}))
                    [(random-unloading {:scheduled-at (at now 11)})]
                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 12)
                                                    :status :finished}))
                    (repeatedly (rand-int 2)
                                #(random-unloading {:scheduled-at (at now 13)
                                                    :line (inc (rand-int 2))
                                                    :status :finished}))
                    [(random-unloading {:scheduled-at (at now 13) :status :allowed-entry :line 10})
                     (random-unloading {:scheduled-at (at now 13) :status :allowed-entry :line 8})]

                    (repeatedly (+ 2 (rand-int 2))
                                #(random-unloading {:scheduled-at (at now 13)
                                                    :line (+ 3 (rand-int 3))
                                                    :status :started}))

                    (repeatedly (+ 4 (rand-int (- (:compound/booking-slots cc) 4)))
                                #(random-unloading {:scheduled-at (at now 14)
                                                    :status :arrived}))
                    (repeatedly (+ 2 (rand-int 4))
                                #(random-unloading {:scheduled-at (at now 14)}))

                    (repeatedly (inc (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 15)}))
                    (repeatedly (+ 2 (rand-int 3))
                                #(random-unloading {:scheduled-at (at now 15)
                                                    :status :arrived}))

                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 16)}))

                    (repeatedly (+ 3 (rand-int (- (:compound/booking-slots cc) 3)))
                                #(random-unloading {:scheduled-at (at now 17)})))]
    (-> compound-worker-demo-state
        (merge {:now now
                :slots (demo-slots)
                :waiting-queue-unloadings (filter (fn [u] (and (:unloading/arrival-datetime u)
                                                               (not (:unloading/start-datetime u))
                                                               (not (:unloading/line u))))
                                                  unloadings)
                :unloadings unloadings}))))

(defscene show-bookings-overview
  :params (atom {:unloadings (repeatedly 5 random-unloading)
                 :compound-config (cc/get-compound-config "Piadena")})
  [!state]
  [:div.border
   [bookings-overview !state]])

(defscene show-bookings-empty
  :params (atom {:unloadings []
                 :compound-config (cc/get-compound-config "Piadena")})
  [!state]
  [:div.border
   [bookings-overview !state]])

(defn render-bookings-overview [_result _match]
  (let [{:keys [loading error result]} @deframe/!current-route-fetch]
    [bookings-overview result]))

(defscene show-receptionist-overview
  :params (atom receptionist-demo-state)
  [!state]
  [:div.border
   [receptionist-overview receptionist-demo-state]])

(defn render-unloading-reception [_result {:as _match :keys [path-params]}]
  (r/with-let [!now (r/atom (js/Date.))]
    (let [compound (:compound-name path-params)
          {:keys [loading error result]} @deframe/!current-route-fetch]
      (hooks/use-effect #(letfn [(current-time-loop-fn []
                                   (reset! !now (js/Date.))
                                   (js/setTimeout current-time-loop-fn 1000))]
                           (current-time-loop-fn)))
      (cond result
            (let [{:keys [companies waiting-queue-unloadings all-unloadings upcoming-slots]} result]
              [receptionist-overview
               {:compound-config (cc/get-compound-config compound)
                :compound compound
                :companies companies
                :now @!now
                :slots upcoming-slots
                :waiting-queue-unloadings waiting-queue-unloadings
                :unloadings all-unloadings}])

            loading
            [:div.bg-white.md:bg-gray-200.min-h-screen [ui/loading-spinner]]

            error
            [ui/error-view error]))))

(defscene receptionist-mark-arrived
  :params (atom (assoc receptionist-demo-state
                       :current-unloading {:unloading/code "ABAU123GG1"
                                           :unloading/scheduled-duration 3600
                                           :unloading/scheduled-datetime #inst"2022-01-21T10:00:00.000-00:00"
                                           :transport/license-plate "FF123LO"
                                           :transport/number "GG123"
                                           :transport/driver-name (random-driver-name)
                                           :transport/driver-phone "+393333123456"
                                           :transport/vehicles [{:vehicle/new? false}
                                                                {:vehicle/new? false}
                                                                {:vehicle/new? true :vehicle/make {:make/name "BMW"}}
                                                                {:vehicle/new? true :vehicle/make {:make/name "VW"}}]}))
  [!state]
  [:div.border
   [receptionist-overview @!state]])

(defscene receptionist-new-unloading
  :params (atom (assoc receptionist-demo-state :new-unloading {}))
  [!state]
  [:div.border
   [receptionist-overview @!state]])

(defscene compound-worker-overview
  [:div.border
   {:style {:height 600}}
   [manage-unloading-status compound-worker-demo-state]])

(defscene compound-worker-overview-selected
  [:div.border
   {:style {:height 600}}
   [manage-unloading-status (assoc compound-worker-demo-state :selected-unloading (rand-nth (:unloadings compound-worker-demo-state)))]])

(defn render-unloading-manage-status [_result {:as _match :keys [path-params]}]
  (r/with-let [!now (r/atom (js/Date.))]
    (let [{:keys [loading error result]} @deframe/!current-route-fetch
          {:keys [unloadings companies]} result
          compound-name (:compound-name path-params)]
      (hooks/use-effect #(letfn [(current-time-loop-fn []
                                   (reset! !now (js/Date.))
                                   (js/setTimeout current-time-loop-fn 1000))]
                           (current-time-loop-fn)))
      [:div
       [ui/top-bar]
       [manage-unloading-status {:compound-config (cc/get-compound-config compound-name)
                                 :now @!now
                                 :compound compound-name
                                 :unloadings unloadings
                                 :companies companies}]])))

(defn record-unloading-form [{:keys [compound companies compound-config] :as props}]
  (r/with-let [add-new-company? (deframe/!route-get ::add-new-company?)]
    (with-form [!form {:unloading/company-name ?company-name
                       :new-company/name ?new-company-name
                       :new-company/vat-id ?new-company-vat-id
                       :booking/day ?booking-day
                       :booking/time ?booking-time
                       :transport/license-plate ?transport-plate
                       :transport/vehicles (vec (concat
                                                 (if (:booking/used-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?used-vehicles-with-customers :many {:vehicle/new? false
                                                                                          :vehicle/number (?vehicle-number :init 1)
                                                                                          :fahrzeug/customer {:customer/name ?customer-name}}))
                                                   (when ?used-vehicles
                                                     (take (js/parseInt ?used-vehicles)
                                                           (repeat {:vehicle/new? false}))))

                                                 (cond
                                                   (:booking/new-vehicle-make-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-make :many {:vehicle/new? true
                                                                                    :vehicle/number (?vehicle-number :init 1)
                                                                                    :vehicle/make {:make/name ?make-name}}))

                                                   (:booking/new-vehicle-customer-required? compound-config)
                                                   (mapcat
                                                    (fn [{:as vehicle :vehicle/keys [number] :or {number 0}}]
                                                      (repeat number (dissoc vehicle :vehicle/number)))
                                                    (?new-vehicles-with-customer :many {:vehicle/new? true
                                                                                        :vehicle/number (?vehicle-number :init 1)
                                                                                        :fahrzeug/customer {:customer/name ?customer-name}}))

                                                   :else
                                                   (when ?new-vehicles
                                                     (take (js/parseInt ?new-vehicles)
                                                           (repeat {:vehicle/new? true}))))))}
                :required [?transport-plate ?booking-time]
                :meta {'?booking-day {:validators [(fn [booking-day _]
                                                     (when (str/blank? booking-day)
                                                       {:type :invalid
                                                        :content "Please select a date"}))]}
                       '?new-company-vat-id {:validators [(fn [vat-id _]
                                                            (when (and (not (str/blank? vat-id))
                                                                       (not (contains? auth/vat-cc (apply str (take 2 vat-id)))))
                                                              {:type :invalid
                                                               :content (tr "Please use the international prefix: e.g. IT1234567")}))]}}
                :form/validators (conj unloading-validators
                                       (fn [form-data _]
                                         (when-not (or (:unloading/company-name form-data)
                                                       (and (:new-company/name form-data)
                                                            (:new-company/vat-id form-data)))
                                           {:type :invalid
                                            :content (tr "Specify a company or create a new one")})))]
      [:form.flex.flex-col.relative.rounded-lg.max-w-3xl.mx-auto
       {:on-submit #(do (.preventDefault %)
                        (if (forms/submittable? !form)
                          (forms/watch-promise
                           !form
                           (p/let [result (http/$lambda 'ductile.truck-booking/handle-record
                                                        {:compound-name compound
                                                         :unloading (assoc @!form
                                                                           :unloading/scheduled-duration 3600 ;; TODO not hardcode it?
                                                                           :unloading/scheduled-datetime (:booking/time @!form))})]
                             (if-not (:error result)
                               (do
                                 (deframe/route-dissoc-in! [:new-unloading])
                                 (deframe/route-refetch!)
                                 nil)
                               (forms/message :error (:error-message result)))))
                          (forms/touch! !form)))}
       [:div.border-b.px-6.py-4
        [:h4.mt-0.text-base (tr "Add new unloading")]]
       [:div.flex-auto.px-6.pt-6.bg-slate-100
        [:div.md:flex.md:items-center
         {:class (if @add-new-company? "mb-2" "mb-3")}
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (if @add-new-company?
            [:div.inline
             (tr "Add a new company")
             [:span.font-normal.text-xs " " (tr "or") " "
              [:a.text-indigo-600.hover:underline.font-bold
               {:href "#" :on-click #(do (.preventDefault %)
                                         (.stopPropagation %)
                                         (deframe/route-update! ::add-new-company? not))}
               (tr "show me the list of existing companies instead")]]]
            (tr "Company"))]
         [:div.border-b.flex-auto.hidden.md:block {:style {:height 1}}]]
        (if @add-new-company?
          [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Company name")]
             [forms.ui/input-text ?new-company-name
              {:class "w-full"}]]]
           [:div
            [ui/with-label
             [:span.text-xs (tr "VAT ID")]
             [forms.ui/input-text ?new-company-vat-id
              {:class "w-full"}]]]]
          [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Select a company")]
             [forms.ui/combo-box ?company-name
              {:class "text-sm mr-2 w-full"
               :data-testid "company-select"
               :options (->> companies
                             (sort)
                             (map (fn [p] {:label p :value p})))}]]]
           [:div
            [:p.text-xs.mt-0.text-slate-600.mt-6.pt-1
             (tr "Couldn’t find a company in this list?")
             [:br]
             [:a.font-bold.text-indigo-600.hover:underline
              {:href "#" :on-click #(do (.preventDefault %)
                                        (.stopPropagation %)
                                        (deframe/route-update! ::add-new-company? not))}
              (tr "Add a new company")]]]])
        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Booking Date")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
         [:div
          [ui/with-label
           [:span.text-xs (tr "Arrival day")]
           [forms.ui/input-text ?booking-day {:type "date"
                                              :data-testid "arrival-day"
                                              :max (->iso-date (minus (js/Date.) [1 :days]))
                                              :on-change (fn [e] (reset! ?booking-day (j/get-in e [:target :value])))}]]]

         [:div
          (when @?booking-day
            [ui/with-label
             [:span.text-xs (tr "Time slot")]
             {:data-testid "time-slot-select"}
             [forms.ui/combo-box ?booking-time
              {:class "text-sm mr-2 w-full"
               :options (let [[start-hour end-hour] [0 23]]
                          (->> (range start-hour end-hour)
                               (map (fn [p] (let [d (at (js/Date. @?booking-day) p 0 0 0)]
                                              {:label (str (time-format/->str {:format :time} d)
                                                           " – "
                                                           (time-format/->str {:format :time} (plus d [3600 :seconds])))
                                               :data-testid (str "time-slot-select-" p)
                                               :value d})))))}]])]]

        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Truck")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-3.mb-6
         [:div
          [ui/with-label
           [:span.text-xs (tr "License plate")]
           [forms.ui/input-text ?transport-plate
            {:class "w-full"
             :data-testid "license-plate-input"}]]]]

        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "Used vehicles")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        (if (:booking/used-vehicle-customer-required? compound-config)
          [:<>

           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?used-vehicles-with-customers]
                   [used-vehicle-form-with-customer compound-config ?vehicle]))

           ;; add used vehicle by customer
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Customer")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-customer compound-config
               {:on-value #(forms/add-many! ?used-vehicles-with-customers {'?customer-name %})}]]]]]
          [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Number of used vehicles")]
             [forms.ui/input-text ?used-vehicles
              {:type "number" :min 0 :max 12}]]]])


        [:div.flex.items-center.mb-3
         [:h4.text-sm.text-gray-500.mr-3.leading-none.flex-shrink-0.mt-0
          (tr "New vehicles")]
         [:div.border-b.flex-auto {:style {:height 1}}]]
        (cond
          (:booking/new-vehicle-make-required? compound-config)
          [:<>
           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?new-vehicles-with-make]
                   [new-vehicle-form-with-make ?vehicle]))

           ;; add new vehicle by make
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Make")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-make {:on-value #(forms/add-many! ?new-vehicles-with-make {'?make-name %})}]]]]]

          (:booking/new-vehicle-customer-required? compound-config)
          [:<>
           ;; show existing new vehicle entries
           (into [:<>]
                 (for [?vehicle ?new-vehicles-with-customer]
                   [new-vehicle-form-with-customer compound-config ?vehicle]))

           ;; add new vehicle by make
           [:div.md:grid.md:grid-cols-2.gap-3.mb-3
            [ui/with-label
             [:span.text-xs (tr "Customer")]
             [:div.w-full.mb-3.text-sm
              [select-vehicle-customer props
               {:on-value #(forms/add-many! ?new-vehicles-with-customer {'?customer-name %})}]]]]]
          :else
          [:div.grid.grid-cols-1.md:grid-cols-1.gap-3.mb-6
           [:div
            [ui/with-label
             [:span.text-xs (tr "Number of new vehicles")]
             [forms.ui/input-text ?new-vehicles
              {:type "number" :min 0 :max 12}]]]])]

       (into [:div.border-t.py-2.px-6] (map forms.ui/view-message) (forms/visible-messages !form))
       [:div.pb-4.px-6.md:flex.justify-between.items-center
        [ui/button {:variant :primary
                    :size 3
                    :class "flex-shrink-0"}
         (tr "Record unloading")]]])))

(defn slots-overview-table [{:keys [^js date slots unloadings compound-name editing? refresh-slots!] :as props}]
  (r/with-let [!state (r/atom {})]
    (hooks/use-effect (fn [] (when-not editing? (reset! !state nil))) [editing?])
    (let [week (->> (range 7)
                    (map (fn [n]
                           (-> date
                               (.startOf "week")
                               (.plus #js {:days n})))))
          [from-hour to-hour] (if editing?
                                [0 24]
                                (->> slots ; render only earliest to latest slot of week
                                     (filter (fn [[iso-day]] (.hasSame ^js (.fromISO ^js DateTime iso-day) date "week")))
                                     vals (map keys) flatten sort ((juxt first (comp inc last)))))
          day-open? (fn [^js day]
                      (seq (get slots (.toISODate day))))
          day-editable? (fn [day] (and editing? (<= (.startOf ^js (.now ^js DateTime) "day") day)))
          [ed-h-from ed-h-to] (let [s @!state] (sort [(:h-from s) (:h-to s)]))
          editing-slot? (fn [iso-day h]
                          (and editing?
                               (= iso-day (:iso-day @!state))
                               (<= ed-h-from h ed-h-to)))
          set-special-slots!
          (fn [{:keys [iso-day h-from h-to slots-amount]}]
            (deframe/call-lambda
             {:on-result refresh-slots!}
             'ductile.truck-booking/handle-set-special-slots+times
             {:compound/name compound-name
              :iso-day iso-day
              :h-from h-from
              :h-to h-to
              :slots-amount slots-amount}))
          save-edits!
          (fn []
            (let [{:keys [h-from h-to new-slot-amount iso-day]} @!state
                  [h-from h-to] (sort [h-from h-to])
                  h-to (inc h-to)]
              (when (and iso-day h-from h-to new-slot-amount)
                (set-special-slots!
                 {:iso-day iso-day
                  :h-from h-from
                  :h-to h-to
                  :slots-amount new-slot-amount}))))
          close-day!
          (fn [iso-day]
            (set-special-slots!
             {:iso-day iso-day
              :h-from 0
              :h-to 24
              :slots-amount 0}))
          on-key-down
          (fn [^js e]
            (when (and editing? (:h-from @!state))
              #_(js/console.log e)
              (let [k (fn [] (.-preventDefault e) (.-stopPropagation e))]
                (cond
                  ((set (map (partial str "Digit") (range 0 10))) (.-code e))
                  (do (k) (swap! !state assoc :new-slot-amount (-> (.-code e) (str/replace "Digit" "") js/parseInt)))

                  (#{"Backspace" "Delete"} (.-code e))
                  (do (k) (swap! !state assoc :new-slot-amount 0))

                  (= "ArrowDown" (.-code e)) (do (k) (if (.-shiftKey e) (swap! !state assoc :h-to 23) (swap! !state update :h-to inc)))
                  (= "ArrowUp" (.-code e)) (do (k) (if (.-shiftKey e) (swap! !state assoc :h-to 0) (swap! !state update :h-to dec)))

                  (= "Escape" (.-code e))
                  (do (k) (if (empty? @!state)
                            (deframe/route-assoc! ::editing? false)
                            (reset! !state nil)))

                  (= "Enter" (.-code e))
                  (do (k) (save-edits!) (reset! !state nil))))))
          on-pointer-up
          (fn [e]
            (cond
              (and (not (:dragging? @!state)) ; on click away
                   (:new-slot-amount @!state))
              (do (save-edits!) (reset! !state nil))

              (:dragging? @!state)
              (swap! !state assoc :dragging? false)))]
      (hooks/use-effect
       (fn []
         (js/addEventListener "keydown" on-key-down)
         (js/addEventListener "pointerup" on-pointer-up)
         #(do (js/removeEventListener "keydown" on-key-down)
              (js/removeEventListener "pointerup" on-pointer-up)))
       [editing? date])
      [:<>
       (when (ui/debug-mode?) [render/inspect [@!state slots]])
       [:div.mt-6
        [:table.tabular-nums.border-separate.w-full.border-spacing-1.bg-white.rounded
         {:on-double-click #(deframe/route-assoc! ::editing? true)}
         [:thead
          [:tr
           (into
            [:<> [:th {:class "w-[150px]"}]]
            (for [^js day week
                  :let [iso-day (.toISODate day)]]
              [:th.text-xs.px-5.py-2.text-xs.font-bold.text-gray-700.uppercase.tracking-wider.rounded
               {:scope "col"
                :class (when (.hasSame day ^js (.now ^js DateTime) "day") "bg-indigo-50")}

               [:div.flex.flex-col.justify-start.items-center
                [:div.text-gray-400     ; weekday
                 (-> day
                     (.setLocale (get-current-iso-locale))
                     (.toFormat "ccc"))]
                [:div.mb-2
                 (time-format/->str {:format :date} day)]
                (when editing?
                  [ui/button
                   {:variant (if (day-open? day) :destructive-white :white)
                    :disabled? (not (day-open? day))
                    :class (when-not (day-editable? day) ["invisible"]) ; keep taking up height
                    :on-click #(close-day! iso-day)}
                   (if (day-open? day)
                     (tr "Close")
                     (tr "Closed"))])]]))]]

         (into
          [:tbody
           {:class (when editing? "select-none")}]
          (for [h (range from-hour to-hour)]
            (into
             [:tr.hover:bg-indigo-50.text-gray-500.hover:text-black.rounded
              [:th.text-xs.px-5.py-2.text-xs.font-bold.rounded.uppercase.tracking-wider
               {:class "w-[150px]"}
               (format-slot-time {:break? false} (hour->datetime h) [3600 :seconds])]]
             (for [^js day week
                   :let [iso-day (.toISODate day)
                         {:keys [amount booked-amount] :as slot} (get-in slots [iso-day h])
                         available-amount (when amount (- amount booked-amount))]]
               [:td.h-100.text-center.text-sm.font-bold.rounded
                (merge
                 {:class [(cond
                            (editing-slot? iso-day h) "bg-indigo-200 text-white"
                            (nil? available-amount) "text-gray-400 bg-gray-100"
                            (<= available-amount 0) "text-red-500 bg-red-100 hover:bg-red-200"
                            (= 1 available-amount) "text-yellow-500 bg-yellow-100 hover:bg-yellow-200"
                            (= 2 available-amount) "text-green-500 bg-green-100 hover:bg-green-200"
                            (= 3 available-amount) "text-green-600 bg-green-200 hover:bg-green-300"
                            :else "text-green-800 bg-green-300 hover:bg-green-400")
                          (when (day-editable? day) "hover:bg-indigo-300 text-white cursor-crosshair")]}
                 (when editing?
                   {:on-pointer-down
                    (fn []
                      (cond
                        (:new-slot-amount @!state) (do (save-edits!) (reset! !state nil))
                        (day-editable? day) (swap! !state assoc
                                                   :dragging? true
                                                   :iso-day iso-day :h-from h :h-to h
                                                   :new-slot-amount (or (:new-slot-amount @!state)
                                                                        amount))))
                    :on-mouse-enter #(when (:dragging? @!state) (swap! !state assoc :h-to h))}))

                (if (day-editable? day)
                  [:span.inline-block.rounded.bg-white.text-gray-500.shadow-inner.cursor-text.px-4.min-w-16.text-center
                   {:class (when (editing-slot? iso-day h) "ring-2 ring-indigo-500")}
                   (or (if (editing-slot? iso-day h)
                         (:new-slot-amount @!state)
                         amount)
                                        ; force min height
                       [:span.invisible "0"])]
                  (or available-amount "-"))]))))]]])))

(def !slots-overview-date
  (deframe/!route-get ::selected-date (.now ^js DateTime)))

(def !slots-overview-editing?
  (deframe/!route-get ::editing? false))

(defn unloadings-inspector [{:keys [height unloadings compound-config] :as props}]
  (let [new-unloading @(deframe/!route-get :new-unloading)
        selected-customer @(deframe/!route-get :selected-customer)
        selected-company @(deframe/!route-get :selected-company)
        selected-make @(deframe/!route-get :selected-make)
        query @(deframe/!route-get :query)
        end-date @(deframe/!route-get :end-date (->iso-date (js/Date.)))
        start-date @(deframe/!route-get :start-date (->iso-date (js/Date.)))
        {:booking/keys [new-vehicle-customer-required? used-vehicle-customer-required? new-vehicle-make-required?]} compound-config
        filtered-unloadings (cond->>
                              unloadings

                              selected-company
                              (filter (fn [u] (= selected-company (get-in u [:unloading/company :company/name]))))

                              query
                              (filter (fn [u] (or (str/includes? (:transport/driver-name u) query)
                                                  (str/includes? (:transport/license-plate u) query))))

                              selected-make
                              (filter (fn [u] (contains? (->> (:transport/vehicles u)
                                                              (map (comp :make/name :vehicle/make))
                                                              (set))
                                                         selected-make)))
                              selected-customer
                              (filter (fn [u] (contains? (->> (:transport/vehicles u)
                                                              (map (comp :customer/name :fahrzeug/customer))
                                                              (set))
                                                         selected-customer))))]
    [:div
     {:class (when-not height "min-h-screen")
      :style (when height {:height height :overflow-y "auto"})}
     (when new-unloading
       [ui/modal {:on-close #(deframe/route-dissoc! :new-unloading)
                  :attrs {:class "w-[800px]"}}
        [record-unloading-form props]])
     [:div
      [:div.flex.items-center.justify-between
       [:h4.m-0.font-bold.text-lg (tr "Unloadings")]
       [:div.flex.items-center.gap-2
        [ui/button
         {:variant :white
          :on-click #(router/navigate-to (router/path '[unloadings :compound-name slots-overview] {:compound-name (util/current-compound-name)}))}
         [ui/icon :calendar]
         (tr "Opening hours")]
        [ui/button
         {:variant :primary
          :class "mr-4 flex items-center flex-shrink-0"
          :on-click #(deframe/route-assoc! :new-unloading {})}
         [ui/icon {:size 18} :plus]
         (tr "Unloading")]]]
      [:div.my-4.sm:flex.sm:items-center.flex-wrap
       [:div.flex.items-center.justify-between
        [:div
         [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Start Date")]
         [:div.sm:pr-6 [ui/input {:type "date"
                                  :value start-date
                                  :on-change (fn [e]
                                               (let [value (j/get-in e [:target :value])]
                                                 (if (str/blank? value)
                                                   (deframe/route-dissoc! :start-date)
                                                   (do (deframe/route-assoc! :start-date value)
                                                       (deframe/route-assoc! :end-date value)))))
                                  :size 3}]]]
        [:div
         [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "End Date")]
         [:div.sm:pr-6 [ui/input {:type "date"
                                  :value end-date
                                  :min start-date
                                  :on-change (fn [e]
                                               (let [value (j/get-in e [:target :value])]
                                                 (if (str/blank? value)
                                                   (deframe/route-assoc! :end-date start-date)
                                                   (deframe/route-assoc! :end-date value))))
                                  :size 3}]]]]
       [:div
        [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Company")]
        [:div.sm:pr-6 [combobox/view
                       {:size 3
                        :value (if selected-company
                                 {:ui/label selected-company :ui/value selected-company}
                                 {:ui/label (tr "All companies") :ui/value :all})
                        :on-change (fn [e]
                                     (let [value (:ui/value e)]
                                       (if (= :all value)
                                         (deframe/route-dissoc! :selected-company)
                                         (deframe/route-assoc! :selected-company value))))
                        :choices (into [{:ui/label (tr "All companies") :ui/value :all}]
                                       (->> unloadings
                                            (map (comp :company/name :unloading/company))
                                            (remove str/blank?)
                                            (set)
                                            (map (fn [cn] {:ui/label cn :ui/value cn}))
                                            (sort-by :ui/label)))}]]]

       [:div
        [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Driver / License Plate")]
        [:div.pr-6 [ui/input
                    {:placeholder (tr "Driver / License Plate")
                     :on-change (fn [e]
                                  (let [value (j/get-in e [:target :value])]
                                    (if (str/blank? value)
                                      (deframe/route-dissoc! :query)
                                      (deframe/route-assoc! :query value))))
                     :size 3}]]]
       (when (or used-vehicle-customer-required?
                 new-vehicle-customer-required?)
         [:div
          [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Customer")]
          [:div.sm:pr-6 [combobox/view
                         {:size 3
                          :value (if selected-customer
                                   {:ui/label selected-customer :ui/value selected-customer}
                                   {:ui/label (tr "All customers") :ui/value :all})
                          :on-change (fn [e]
                                       (let [value (:ui/value e)]
                                         (if (= :all value)
                                           (deframe/route-dissoc! :selected-customer)
                                           (deframe/route-assoc! :selected-customer value))))
                          :choices (into [{:ui/label (tr "All customers") :ui/value :all}]
                                         (->> unloadings
                                              (mapcat :transport/vehicles)
                                              (map (comp :customer/name :fahrzeug/customer))
                                              (remove str/blank?)
                                              (set)
                                              (map (fn [cn] {:ui/label cn :ui/value cn}))
                                              (sort-by :ui/label)))}]]])
       (when new-vehicle-make-required?
         [:div
          [:div.py-3.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Make")]
          [:div.sm:pr-6 [combobox/view
                         {:size 3
                          :value (if selected-make
                                   {:ui/label selected-make :ui/value selected-make}
                                   {:ui/label (tr "All makes") :ui/value :all})
                          :on-change (fn [e]
                                       (let [value (:ui/value e)]
                                         (if (= :all value)
                                           (deframe/route-dissoc! :selected-make)
                                           (deframe/route-assoc! :selected-make value))))
                          :choices (into [{:ui/label (tr "All makes") :ui/value :all}]
                                         (->> unloadings
                                              (mapcat :transport/vehicles)
                                              (map (comp :make/name :vehicle/make))
                                              (remove str/blank?)
                                              (set)
                                              (map (fn [cn] {:ui/label cn :ui/value cn}))
                                              (sort-by :ui/label)))}]]])
       [:div.flex.grow.justify-end
        [:form.mt-4.sm:mt-12
         {:action (str "/unloadings/"
                       (util/current-compound-name)
                       "/inspect/export") ;; FIXME this should not be using str but it's a backend-only route so not available to reitit
          :method :get}
         [:input.hidden {:name "start-date" :value start-date}]
         [:input.hidden {:name "end-date" :value end-date}]
         [:button.border.border-indigo-600.rounded-md.px-2.py-1.flex.items-center.text-indigo-600.text-sm.hover:bg-indigo-50
          {:type "submit"}
          [ui/icon {:size 18 :class "mr-1"} :download]
          "CSV"]]]]
      [:div.shadow.overflow-x-auto.border-b.border-slate-200.sm:rounded-lg.text-sm
       [:table.min-w-full.divide-y.divide-slate-200.md:table.overflow-x-auto.bg-slate-100
        [:thead.bg-slate-50
         [:tr.border-b
          [:th.px-3.py-2.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider (tr "Date")]
          [:th.px-3.py-2.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.border-x (tr "Slot")]
          [:th.px-3.py-2.text-center.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.border-x {:col-span 3} (tr "Compound")]
          [:th.px-3.py-2.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.border-r (tr "Code")]
          [:th.px-3.py-2.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.border-r (tr "Company")]
          [:th.px-3.py-2.text-left.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.whitespace-nowrap (tr "License plate")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.text-center.border-x {:col-span 3} (tr "Vehicles")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.uppercase.tracking-wider.text-right (tr "Notes")]]
         [:tr
          [:th.border-r ""]
          [:th.border-r ""]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.tracking-wider.text-left (tr "Arrival")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.tracking-wider.text-left (tr "Start")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.tracking-wider.text-left.border-r (tr "End")]
          [:th.border-r ""]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.tracking-wider.text-left.border-r (tr "Booked by")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.text-left.border-x.whitespace-nowrap (tr "Driver name")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.text-left.border-x  (tr "Used")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.text-center.border-x (tr "New")]
          [:th.px-3.py-2.text-xs.font-medium.text-gray-500.text-right.border-x (tr "Total")]

          [:th ""]]]
        (if (seq filtered-unloadings)
          (into
           [:tbody.bg-white.divide-y.divide-slate-200]
           (map
            (fn [{:keys [ductile/created-by]
                  :transport/keys [vehicles license-plate]
                  :unloading/keys [arrival-datetime start-datetime end-datetime scheduled-datetime scheduled-duration] :as unloading}]
              (let [used-vehicles (remove :vehicle/new? vehicles)
                    new-vehicles (filter :vehicle/new? vehicles)]
                [:tr.even:bg-slate-50.text-xs
                 [:td.px-3.py-2.text-left.border-r
                  (time-format/->str {:format :date} scheduled-datetime)]
                 [:td.px-3.py-2.border-r.whitespace-nowrap.text-left
                  (str (time-format/->str {:format :time} scheduled-datetime)
                       " – "
                       (time-format/->str {:format :time} (plus scheduled-datetime [scheduled-duration :seconds])))]
                 [:td.px-3.py-2 (when arrival-datetime (time-format/->str {:format :time} arrival-datetime))]
                 [:td.px-3.py-2 (when start-datetime (time-format/->str {:format :time} start-datetime))]

                 [:td.px-3.py-2.border-r (when end-datetime (time-format/->str {:format :time} end-datetime))]
                 [:td.px-2.py-2.text-left.border-r (:ductile/code unloading)]
                 [:td.px-3.py-2.text-left.border-r
                  [:div (get-in unloading [:unloading/company :company/name])]
                  [:a.font-bold.text-indigo-400.hover:text-indigo-600 {:href (str "mailto:" (-> created-by :user/email))} (-> created-by :user/name)]]
                 [:td.px-3.py-2.text-left.border-r
                  [:span.font-bold license-plate]
                  [:div (:transport/driver-name unloading)]]
                 [:td.px-3.py-2.border-r.text-left
                  (interpose ", " (for [[customer-name vehicles] (group-by (comp :customer/name :fahrzeug/customer) used-vehicles)]
                                    (str (count vehicles) (when customer-name (str " " customer-name)))))]
                 [:td.px-3.py-2.border-r.text-left
                  (cond  new-vehicle-customer-required?
                         (interpose ", " (for [[customer-name vehicles] (group-by (comp :customer/name :fahrzeug/customer) new-vehicles)]
                                           (str (count vehicles) (when customer-name (str " " customer-name)))))
                         new-vehicle-make-required?
                         (interpose ", "
                                    (for [[make-name vehicles] (group-by (comp :make/name :vehicle/make) new-vehicles)]
                                      (str (count vehicles) (when make-name (str " " make-name)))))
                         :else (count new-vehicles))]
                 [:td.px-3.py-2.border-r.text-right
                  (count vehicles)]


                 [:td.px-3.py-2.text-right
                  (when (and scheduled-datetime arrival-datetime
                             (not= (->iso-date scheduled-datetime)
                                   (->iso-date arrival-datetime)))
                    (str (tr "Handled on:") " " (time-format/->str {:format :date} arrival-datetime)))
                  (when (= :unloading/canceled (:unloading/ending-cause unloading))
                    (tr "Canceled"))]]))
            (sort-by :unloading/scheduled-datetime filtered-unloadings)))
          [:tbody.bg-white.divide-y.divide-slate-200
           [:tr
            [:td.min-w-full.p-5.text-center.font-bold.text-gray-500.text-sm {:col-span 11}
             (tr "No unloadings with the selected criteria.")]]])
        [:tfoot
         (let [total-vehicles (->> filtered-unloadings (mapcat :transport/vehicles))
               new-grouping (if new-vehicle-customer-required?
                              (comp :customer/name :fahrzeug/customer)
                              (comp :make/name :vehicle/make))
               used-grouping (if used-vehicle-customer-required?
                               (comp :customer/name :fahrzeug/customer)
                               (comp :make/name :vehicle/make))
               total-used-vehicles (->> total-vehicles (remove :vehicle/new?) (map used-grouping) (frequencies))
               total-new-vehicles (->> total-vehicles (filter :vehicle/new?) (map new-grouping) (frequencies))]
           [:tr
            [:th.border-r ""]
            [:th.border-r ""]
            [:th.border-r ""]
            [:th.border-r ""]
            [:th.border-r ""]
            [:th.border-r ""]
            [:th.border-r (tr "Total")]
            [:th.px-3.py-2.text-xs.text-left.border-x (into [:ul] (map (fn [[m amount]] [:li (str amount " " m)]) total-used-vehicles))]
            [:th.px-3.py-2.text-xs.text-left.border-x (into [:ul] (map (fn [[m amount]] [:li (str amount " " m)]) total-new-vehicles))]
            [:th.px-3.py-2.text-xs.border-x (count total-vehicles)]
            [:th ""]])]]]]]))


(def inspector-demo-data
  (concat (repeatedly 10 random-unloading)
          (repeatedly 5 #(random-unloading {:base-day (minus (js/Date.) [1 :days]) :status :arrived}))
          (repeatedly 10 #(random-unloading {:base-day (minus (js/Date.) [1 :days]) :status :started}))
          (repeatedly 10 #(random-unloading {:base-day (minus (js/Date.) [1 :days]) :status :finished}))
          (repeatedly 5 #(random-unloading {:base-day (js/Date.) :status :arrived}))
          (repeatedly 10 #(random-unloading {:base-day (js/Date.) :status :started}))
          (repeatedly 10 #(random-unloading {:base-day (js/Date.) :status :finished}))
          (repeatedly 10 #(random-unloading {:base-day (plus (js/Date.) [1 :days])}))))

(def demo-bookable-slots
  (vec (group-by (comp ->iso-date :datetime) (repeatedly 240 (fn [] {:datetime (-> (js/Date.)
                                                                                   (plus [(rand-int 7) :days])
                                                                                   (at (rand-int 24)))
                                                                     :duration 3600
                                                                     :amount (rand-nth (range -2 5))})))))

(defn render-unloadings-inspector [_result {:as _match :keys [path-params]}]
  (let [compound-name (:compound-name path-params)
        start-date @(deframe/!route-get :start-date (->iso-date (js/Date.)))
        end-date @(deframe/!route-get :end-date (->iso-date (js/Date.)))]
    (hooks/use-effect
     #(deframe/call-lambda
       {:result-path [::unloadings-in-range]}
       'ductile.truck-booking/unloadings-in-date-range
       {:compound/name compound-name :start-date start-date :end-date end-date})
     [start-date end-date])
    (let [{:keys [loading error result]} @(deframe/!get ::unloadings-in-range)]
      (cond result
            [unloadings-inspector {:unloadings (:unloadings result)
                                   :compound compound-name
                                   :compound-config (cc/get-compound-config compound-name)
                                   :companies (:companies result)}]

            loading
            [:div.bg-white.md:bg-gray-200.min-h-screen [ui/loading-spinner]]

            error
            [ui/error-view error]))))

(defn render-slots-overview [_result {:keys [path-params]}]
  (let [^js date @!slots-overview-date
        editing? @!slots-overview-editing?
        on-change-day (fn [^js date] (deframe/route-assoc! ::selected-date date))
        slots @(deframe/!get ::slots-overview)
        refresh-slots!
        (fn []
          (deframe/call-lambda
           {:result-path [::slots-overview]}
           'ductile.truck-booking/slots-overview
           {:compound/name (:compound-name path-params)
            :start (.minus ^js (.startOf ^js @!slots-overview-date "week") #js {:weeks 3}) ; preload adjacent
            :end (.plus ^js (.endOf ^js @!slots-overview-date "week") #js {:weeks 2})}))]
    (hooks/use-effect
     refresh-slots!
     [@!slots-overview-date (:compound-name path-params)])

    [:div.p-6
     [:div.flex.justify-between
      [:div.flex.gap-2.flex-1
       [:h2.text-lg.m-0.font-bold
        (tr "Opening hours")]]
      [:div.flex.flex-1.items-center.justify-center.gap-1
       [ui/week-nav date on-change-day]]

      [:div.flex.flex-1.justify-end
       [:div.flex.gap-2
        (if editing?
          [ui/button
           {:variant :primary
            :on-click #(deframe/route-assoc! ::editing? false)}
           [ui/icon :check] [:span.ml-1 (tr "Done")]]
          [ui/button
           {:variant :primary
            :on-click #(deframe/route-assoc! ::editing? true)}
           [ui/icon :pencil2] [:span.ml-1 (tr "Edit")]])]]]

     (cond (not slots)
           [:div.bg-white.md:bg-gray-200
            [ui/loading-spinner]]

           (:error slots)
           [ui/error-view slots]

           (:result slots)
           [slots-overview-table
            {:editing? editing?
             :date date
             :slots (:result slots)
             :unloadings [] #_(:all-unloadings unloadings-results)
             :compound-name (:compound-name path-params)
             :refresh-slots! refresh-slots!}])]))
